import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Code2 } from 'lucide-react';
import MarketingShell from '../components/marketing/MarketingShell';
import Hero from '../components/marketing/Hero';
import WhyChoose from '../components/marketing/WhyChoose';
import FinalCTA from '../components/marketing/FinalCTA';

const defaultDescription = 'idemGO is privacy-first verification infrastructure for hosted onboarding flows, APIs, webhooks, audit trails, manual review, and document signing.';

const lifecycleSteps = [
  ['01', 'Session created', 'API or dashboard creates a hosted verification link and reserves pending usage.'],
  ['02', 'Evidence submitted', 'Applicant uploads ID evidence and selfie in the hosted flow.'],
  ['03', 'Checks run', 'OCR extraction, coded-strip comparison, and face match produce structured evidence.'],
  ['04', 'Review or decision', 'Approved, rejected, needs review, expired, or revoked states are recorded with audit events.'],
  ['05', 'Webhook delivered', 'Your backend receives a signed event and can read normalized lifecycle status.'],
];

const statusRows = [
  ['pending', 'Hosted link created or invitation sent; usage is reserved but not consumed.'],
  ['needs_review', 'Evidence needs an operational review before approval or rejection.'],
  ['approved', 'Identity evidence passed and structured results are available.'],
  ['rejected', 'Verification failed or was manually rejected.'],
  ['revoked / expired', 'The flow is no longer usable, with lifecycle evidence retained.'],
];

const homepageJobs = [
  ['Verify users', 'Hosted ID and selfie flows with OCR extraction, coded-strip comparison, face matching, and structured decisions.'],
  ['Sign documents', 'SES signing for simple flows, or AES signing linked to approved KYC before the document can be completed.'],
  ['Review outcomes', 'Manual review, lifecycle statuses, audit trails, revocation, expiry, and retention controls stay visible.'],
];

const sensitiveRows = [
  ['Raw images', 'Processed, then discarded by default'],
  ['Evidence record', 'Extracted fields and lifecycle status retained'],
  ['Audit trail', 'Review, webhook, revocation, and expiry history'],
  ['Retention controls', 'Policy-driven handling when needed'],
];

function PageHero({ eyebrow, title, description, ctaTo = '/signup', ctaLabel = 'Get started', secondaryCtaTo = '/developers', secondaryCtaLabel = 'View developers' }) {
  return (
    <section className="idemgo-grid-bg">
      <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
        {eyebrow && <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{eyebrow}</p>}
        <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.04] mt-4 max-w-4xl">{title}</h1>
        <p className="text-base md:text-lg text-zinc-600 mt-6 max-w-2xl leading-relaxed">{description}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <Link to={ctaTo} className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
            {ctaLabel} <ArrowRight className="w-4 h-4" strokeWidth={2}/>
          </Link>
          <Link to={secondaryCtaTo} className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
            {secondaryCtaLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}

function Section({ id, eyebrow, title, description, children, tinted = false }) {
  return (
    <section id={id} className={tinted ? 'bg-zinc-50/35' : 'bg-white'}>
      <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
        {eyebrow && <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{eyebrow}</p>}
        <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-10 lg:gap-16 items-start">
          <div className="min-w-0">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">{title}</h2>
            {description && <p className="text-sm md:text-base text-zinc-600 leading-relaxed mt-4 max-w-[65ch]">{description}</p>}
          </div>
          <div className="min-w-0">{children}</div>
        </div>
      </div>
    </section>
  );
}

function FeatureGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      {items.map(([title, text]) => (
        <div key={title} className="border border-zinc-200/80 rounded-lg bg-white p-6 md:p-7">
          <h3 className="font-display text-xl font-semibold tracking-tight">{title}</h3>
          <p className="text-sm text-zinc-600 leading-relaxed mt-3">{text}</p>
        </div>
      ))}
    </div>
  );
}

function Timeline({ steps = lifecycleSteps }) {
  return (
    <div className="border border-zinc-200 rounded-sm bg-white p-6">
      <div className="space-y-0">
        {steps.map(([num, title, text], index) => (
          <div key={title} className="relative pl-12 pb-7 last:pb-0">
            {index < steps.length - 1 && <div className="absolute left-[15px] top-8 bottom-0 w-px bg-zinc-200"/>}
            <div className="absolute left-0 top-0 h-8 w-8 border border-zinc-200 rounded-sm grid place-items-center text-[10px] font-mono text-zinc-500 bg-white">
              {num}
            </div>
            <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
            <p className="text-sm text-zinc-600 leading-relaxed mt-1">{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CodeBlock({ children }) {
  return (
    <pre className="max-w-full font-mono text-xs bg-zinc-900 text-zinc-100 p-5 rounded-sm overflow-x-auto leading-relaxed border border-zinc-900">
      {children}
    </pre>
  );
}

function EventTable({ rows }) {
  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, text]) => (
            <tr key={label} className="border-b border-zinc-100 last:border-0 align-top">
              <td className="px-4 py-3 font-mono text-xs text-zinc-500 w-40">{label}</td>
              <td className="px-4 py-3 text-zinc-700">{text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pipeline() {
  const steps = [
    ['Capture', 'Hosted front/back document and selfie submission'],
    ['Extract', 'OCR fields and coded-strip/MRZ comparison'],
    ['Compare', 'Claimed identity, document evidence, and face match score'],
    ['Decide', 'Approved, rejected, needs review, expired, or revoked'],
  ];
  return (
    <div className="grid md:grid-cols-4 gap-0 border border-zinc-200 rounded-sm overflow-hidden bg-white">
      {steps.map(([title, text], index) => (
        <div key={title} className="p-5 border-b md:border-b-0 md:border-r border-zinc-200 last:border-0">
          <div className="font-mono text-[11px] text-zinc-400">{String(index + 1).padStart(2, '0')}</div>
          <h3 className="font-display text-lg font-semibold tracking-tight mt-3">{title}</h3>
          <p className="text-sm text-zinc-600 leading-relaxed mt-2">{text}</p>
        </div>
      ))}
    </div>
  );
}

function HomepageLink({ to, children }) {
  return (
    <Link to={to} className="inline-flex items-center gap-2 mt-7 text-sm font-medium text-zinc-900 hover:text-zinc-600">
      {children} <ArrowRight className="h-4 w-4" strokeWidth={2}/>
    </Link>
  );
}

function HomepageEyebrow({ children }) {
  return <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{children}</p>;
}

function HomepageVisualPanel({ children, className = '', align = 'right' }) {
  const alignClass = align === 'left' ? 'lg:mr-auto' : 'lg:ml-auto';
  return (
    <div className={`w-full max-w-[560px] ${alignClass} ${className}`}>
      {children}
    </div>
  );
}

function StatusBadge({ children, tone = 'neutral' }) {
  const tones = {
    success: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    warning: 'border-amber-300 bg-amber-50 text-amber-800',
    neutral: 'border-zinc-200 bg-white text-zinc-500',
    dark: 'border-zinc-900 bg-zinc-900 text-white',
  };
  return (
    <span className={`inline-flex h-5 items-center border px-1.5 text-[10px] font-mono uppercase tracking-widest ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

function ProductPanel({ label, badge, children, className = '' }) {
  return (
    <div className={`border border-zinc-200 bg-white rounded-sm overflow-hidden ${className}`}>
      <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-5 py-3">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-400">{label}</p>
        {badge}
      </div>
      {children}
    </div>
  );
}

function HomepageWorkflowPreview() {
  return (
    <ProductPanel label="Onboarding flow" badge={<StatusBadge tone="success">Live</StatusBadge>}>
      <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
        {homepageJobs.map(([title, text], index) => (
          <div key={title} className="p-5">
            <div className="mb-5 flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center border border-zinc-200 bg-zinc-50 text-[10px] font-mono text-zinc-500">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="h-px flex-1 bg-zinc-100"/>
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">{text}</p>
          </div>
        ))}
      </div>
    </ProductPanel>
  );
}

function RetentionModelPanel({ compact = false }) {
  return (
    <ProductPanel label="Retention model" badge={<StatusBadge tone="neutral">Default</StatusBadge>}>
      <div className="px-5 py-5 md:px-6">
        <h3 className="font-display text-xl font-semibold tracking-tight">Evidence without raw-media storage</h3>
      </div>
      <div className={`px-5 md:px-6 ${compact ? 'pb-5' : 'pb-6'} divide-y divide-zinc-100`}>
        {sensitiveRows.map(([label, value]) => (
          <div key={label} className="grid sm:grid-cols-[150px_1fr] gap-2 py-4 first:pt-0 last:pb-0">
            <p className="text-sm font-medium text-zinc-900">{label}</p>
            <p className="text-sm text-zinc-600 leading-relaxed">{value}</p>
          </div>
        ))}
      </div>
    </ProductPanel>
  );
}

function DeveloperPreview() {
  return (
    <div className="grid gap-4 min-w-0">
      <pre className="max-w-full overflow-x-auto rounded-sm border border-zinc-900 bg-zinc-900 p-5 text-xs leading-relaxed text-zinc-100 font-mono">
{`POST /api/v1/verification-sessions
Authorization: Bearer <api_key>

{
  "applicant_email": "lea@acme.eu",
  "send_email": true,
  "sandbox": true
}`}
      </pre>
      <ProductPanel
        label={<span className="inline-flex items-center gap-2"><Code2 className="h-4 w-4" strokeWidth={1.6}/> Webhook result</span>}
        badge={<StatusBadge tone="success">Approved</StatusBadge>}
      >
        <div className="grid gap-2 p-5 text-sm text-zinc-600">
          <p><span className="font-mono text-xs text-zinc-500">event</span> verification.completed</p>
          <p><span className="font-mono text-xs text-zinc-500">status</span> approved</p>
          <p><span className="font-mono text-xs text-zinc-500">signature</span> X-Idemgo-Signature</p>
        </div>
      </ProductPanel>
    </div>
  );
}

function UsageModelPanel() {
  return (
    <ProductPanel label="Included before scaling">
      <div className="p-5 md:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-8">
        {[
          ['10', 'free verifications'],
          ['10', 'free document signings'],
        ].map(([value, label]) => (
          <div key={label}>
            <p className="font-display text-3xl font-bold tracking-tight leading-none">
              {value} <span className="text-base font-semibold tracking-normal text-zinc-700">{label}</span>
            </p>
          </div>
        ))}
        </div>
        <div className="mt-6 border-t border-zinc-200 divide-y divide-zinc-100">
          {[
            ['Sandbox runs', 'Testing flows do not count against included usage.'],
            ['Completed usage', 'Pending reservations move to used only when a flow completes.'],
          ].map(([label, text]) => (
            <div key={label} className="grid sm:grid-cols-[150px_1fr] gap-2 py-4 first:pt-5 last:pb-0">
              <p className="text-sm font-medium text-zinc-900">{label}</p>
              <p className="text-sm text-zinc-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </ProductPanel>
  );
}

function TextStack({ children }) {
  return (
    <div className="space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed">
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="grid gap-2.5 text-sm text-zinc-700">
      {items.map((item, index) => (
        <li key={typeof item === 'string' ? item : index} className="flex items-start gap-3">
          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-zinc-400"/>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function CopyCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white p-6 md:p-8">
      <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-tight">{title}</h3>
      <div className="mt-5 text-sm text-zinc-600 leading-relaxed">{children}</div>
    </div>
  );
}

function IllustrationPlaceholder({ label, className = '' }) {
  return (
    <div className={`rounded-3xl border border-zinc-200/80 bg-zinc-50/70 p-4 ${className}`}>
      <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-zinc-300/70 bg-white/60 p-6 text-center">
        <p className="max-w-[28ch] text-xs font-mono uppercase tracking-widest leading-relaxed text-zinc-400">
          Illustration placeholder — {label}
        </p>
      </div>
    </div>
  );
}

function StatementSection({ children, tinted = false }) {
  return (
    <section className={tinted ? 'bg-zinc-50/35' : 'bg-white'}>
      <div className="max-w-[980px] mx-auto px-6 py-24 md:py-36 text-center">
        <p className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08]">
          {children}
        </p>
      </div>
    </section>
  );
}

function KycClosingCta() {
  return (
    <section className="idemgo-grid-bg">
      <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding flows users trust</h2>
          <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
            <p>Modern identity verification should strengthen trust — not create friction.</p>
            <p>Use idemGO to verify users securely, streamline onboarding, and support compliance workflows with privacy-first verification infrastructure.</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
              Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
            </Link>
            <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
              Contact sales
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function LifecyclePreview() {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
      <div className="grid md:grid-cols-[220px_1fr]">
        <div className="border-b md:border-b-0 md:border-r border-zinc-200 p-5 bg-zinc-50/50">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-400">Session</p>
          <h3 className="font-display text-xl font-semibold tracking-tight mt-2">Hosted verification link</h3>
          <div className="mt-5 space-y-2">
            <StatusBadge tone="warning">Pending</StatusBadge>
            <StatusBadge tone="neutral">Reserved</StatusBadge>
          </div>
        </div>
        <div className="divide-y divide-zinc-100">
          {lifecycleSteps.map(([num, title, text]) => (
            <div key={title} className="grid sm:grid-cols-[70px_1fr] gap-3 px-5 py-4">
              <p className="font-mono text-xs text-zinc-400">{num}</p>
              <div>
                <h4 className="font-display text-base font-semibold tracking-tight">{title}</h4>
                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OneFlowThreeJobs() {
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20">
        <div className="grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-10 lg:gap-16 items-start">
          <div>
            <HomepageEyebrow>ONE FLOW, THREE JOBS</HomepageEyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.06] mt-3">
              The onboarding work stays connected.
            </h2>
            <HomepageLink to="/product">Explore product</HomepageLink>
          </div>
          <HomepageWorkflowPreview/>
        </div>
      </div>
    </section>
  );
}

function SensitiveOnboarding() {
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-10 lg:gap-16 items-center">
        <div className="order-1 lg:order-2">
          <HomepageEyebrow>SENSITIVE ONBOARDING</HomepageEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.06] mt-3">
            Keep the evidence you need. Avoid raw-image storage by default.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 leading-relaxed mt-5 max-w-[62ch]">
            idemGO separates media processing from the records teams need later: extracted fields, lifecycle states, reviewer decisions, signed documents, and webhook history.
          </p>
          <HomepageLink to="/security">View security</HomepageLink>
        </div>
        <HomepageVisualPanel align="left" className="order-2 lg:order-1">
          <RetentionModelPanel/>
        </HomepageVisualPanel>
      </div>
    </section>
  );
}

function DeveloperControl() {
  return (
    <section className="bg-zinc-50/35">
      <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 lg:gap-16 items-center">
        <div className="order-1 lg:order-2">
          <HomepageEyebrow>DEVELOPER CONTROL</HomepageEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.06] mt-3">
            Use links first. Add API and webhooks when you need control.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 leading-relaxed mt-5 max-w-[62ch]">
            Create sessions by API, send hosted public URLs, verify webhook delivery, test with sandbox fixtures, and map each flow to a normalized status.
          </p>
          <HomepageLink to="/developers">View developers</HomepageLink>
        </div>
        <HomepageVisualPanel align="left" className="order-2 lg:order-1">
          <DeveloperPreview/>
        </HomepageVisualPanel>
      </div>
    </section>
  );
}

function SandboxReady() {
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-8 items-center">
        <div>
          <HomepageEyebrow>SANDBOX READY</HomepageEyebrow>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.06] mt-3">
            Test real workflows before usage scales.
          </h2>
          <p className="text-sm md:text-base text-zinc-600 leading-relaxed mt-5 max-w-[62ch]">
            Start with hosted verification and signing flows. Sandbox submissions are not counted, and completed usage stays separate from pending reservations.
          </p>
          <HomepageLink to="/pricing">View pricing</HomepageLink>
        </div>
        <HomepageVisualPanel>
          <UsageModelPanel/>
        </HomepageVisualPanel>
      </div>
    </section>
  );
}

function HomePositioningStatement() {
  return (
    <section className="bg-white">
      <div className="max-w-[980px] mx-auto px-6 py-24 md:py-36 text-center">
        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08]">Modern onboarding infrastructure for digital platforms.</h2>
        <p className="mt-6 text-base md:text-xl text-zinc-600 leading-relaxed">
          Identity verification, business onboarding, and electronic signatures combined into one structured operational workflow layer.
        </p>
      </div>
    </section>
  );
}

function HomeUnifiedWorkflow() {
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
        <IllustrationPlaceholder label="Verification → onboarding → signing → audit visibility" className="order-2 lg:order-1"/>
        <div className="order-1 lg:order-2">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Verification, onboarding, and signing — connected in one workflow</h2>
          <div className="mt-6">
            <TextStack>
              <p>Modern onboarding systems increasingly involve more than isolated verification checks.</p>
              <p>Organizations often need infrastructure that helps them:</p>
              <BulletList items={[
                'verify users',
                'onboard businesses',
                'manage operational reviews',
                'collect signatures',
                'centralize onboarding visibility',
                'support audit-ready workflows',
              ]}/>
              <p>Without structured infrastructure, onboarding operations quickly become fragmented across disconnected systems and operational processes.</p>
              <p>idemGO helps organizations centralize these workflows into one modern onboarding infrastructure layer designed for operational trust and scalable digital operations.</p>
            </TextStack>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeProductPillars() {
  const pillars = [
    [
      'Identity verification',
      'Support onboarding workflows through modern identity verification infrastructure designed for operational visibility and onboarding trust.',
      'Capabilities include hosted verification flows, document verification, selfie verification, and operational onboarding review workflows.',
      '/product/kyc-verification',
    ],
    [
      'Business onboarding',
      'Centralize KYB onboarding workflows involving company verification, representatives, onboarding reviews, and operational visibility.',
      'Designed for marketplaces, financial platforms, workforce operations, and modern digital onboarding systems.',
      '/product/kyb-verification',
    ],
    [
      'Electronic signatures',
      'Connect signing workflows directly to onboarding and verification infrastructure through hosted signing flows and structured audit visibility.',
      'Support operational approvals, onboarding agreements, and identity-linked signing workflows.',
      '/product/electronic-signatures',
    ],
  ];

  return (
    <section className="bg-zinc-50/35">
      <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
        <div className="grid lg:grid-cols-3 gap-6">
          {pillars.map(([title, text, detail, to]) => (
            <Link key={title} to={to} className="group rounded-2xl border border-zinc-200/80 bg-white p-7 md:p-8 hover:border-zinc-400">
              <h3 className="font-display text-2xl font-semibold tracking-tight group-hover:text-zinc-700">{title}</h3>
              <p className="mt-5 text-sm text-zinc-600 leading-relaxed">{text}</p>
              <p className="mt-4 text-sm text-zinc-600 leading-relaxed">{detail}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeUseCasesIndustries() {
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-center">
        <div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for modern digital operations</h2>
          <div className="mt-6">
            <TextStack>
              <p>idemGO supports onboarding and operational trust workflows across:</p>
              <BulletList items={[
                'financial services',
                'crypto platforms',
                'marketplaces',
                'HR & staffing environments',
              ]}/>
              <p>The platform is designed for organizations that require stronger onboarding visibility without introducing unnecessary operational complexity.</p>
            </TextStack>
          </div>
        </div>
        <IllustrationPlaceholder label="Operational onboarding workflows across multiple industries"/>
      </div>
    </section>
  );
}

function HomeDeveloperInfrastructure() {
  return (
    <section className="bg-zinc-50/35">
      <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
        <IllustrationPlaceholder label="Hosted onboarding infrastructure with APIs and operational visibility" className="order-2 lg:order-1"/>
        <div className="order-1 lg:order-2">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Developer-ready onboarding infrastructure</h2>
          <div className="mt-6">
            <TextStack>
              <p>Modern onboarding systems increasingly require more than standalone APIs.</p>
              <p>idemGO provides infrastructure designed for:</p>
              <BulletList items={[
                'hosted onboarding flows',
                'API integrations',
                'webhooks',
                'operational review workflows',
                'audit-ready onboarding visibility',
              ]}/>
              <p>Teams can launch onboarding systems quickly without building verification and signing infrastructure internally.</p>
            </TextStack>
          </div>
          <Link to="/documentation" className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-zinc-900 hover:text-zinc-600">
            View documentation <ArrowRight className="h-4 w-4" strokeWidth={2}/>
          </Link>
        </div>
      </div>
    </section>
  );
}

function HomeSecurityTrust() {
  return (
    <section className="bg-white">
      <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
        <div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Privacy-conscious onboarding infrastructure</h2>
          <div className="mt-6">
            <TextStack>
              <p>Verification and onboarding systems increasingly involve highly sensitive operational and identity information.</p>
              <p>idemGO focuses on:</p>
              <BulletList items={[
                'structured operational workflows',
                'audit-ready visibility',
                'controlled evidence handling',
                'operational transparency',
                'privacy-conscious onboarding systems',
              ]}/>
              <p>This helps organizations create onboarding environments designed around operational trust and scalable onboarding operations.</p>
            </TextStack>
          </div>
        </div>
        <IllustrationPlaceholder label="Operational review workflow and audit visibility"/>
      </div>
    </section>
  );
}

function HomeFinalCta() {
  return (
    <section className="idemgo-grid-bg">
      <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding systems designed for operational trust</h2>
          <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
            <p>Use idemGO to centralize onboarding, verification, signing, and operational visibility into one modern infrastructure layer.</p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
              Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
            </Link>
            <Link to="/documentation" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
              View documentation
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HomePage() {
  return (
    <MarketingShell title="idemGO · Verification Infrastructure" description={defaultDescription}>
      <Hero/>
      <HomePositioningStatement/>
      <HomeUnifiedWorkflow/>
      <HomeProductPillars/>
      <StatementSection>
        Operational trust should feel built into the workflow — not added afterward.
      </StatementSection>
      <HomeUseCasesIndustries/>
      <HomeDeveloperInfrastructure/>
      <HomeSecurityTrust/>
      <HomeFinalCta/>
    </MarketingShell>
  );
}

export function ProductPage() {
  return (
    <MarketingShell title="Product · idemGO" description="Explore idemGO verification infrastructure: hosted KYC, SES and AES signing, manual review, audit trails, lifecycle statuses, sandbox mode, retention controls, APIs, and webhooks.">
      <PageHero
        eyebrow="PRODUCT"
        title="Onboarding infrastructure built around real verification operations."
        description="idemGO combines hosted KYC verification, SES and AES document signing, manual review, audit trails, retention controls, sandbox mode, and lifecycle-aware APIs."
      />
      <Section
        id="verification-lifecycle"
        eyebrow="Verification lifecycle"
        title="From hosted link to structured verification outcome."
        description="A verification session can start from the dashboard or API. Applicants complete the hosted flow, idemGO extracts and compares evidence, and the result becomes a normalized operational status."
      >
        <LifecyclePreview/>
      </Section>
      <Section
        id="evidence"
        eyebrow="Evidence pipeline"
        title="Evidence is structured for operations, not just stored as files."
        description="idemGO tracks the pieces reviewers and backends need: OCR fields, document numbers, coded-strip comparison, selfie/face match outcomes, status changes, and review decisions."
        tinted
      >
        <Pipeline/>
      </Section>
      <Section
        id="signing"
        eyebrow="Signing orchestration"
        title="SES for simple signing. AES when KYC must gate the signature."
        description="Document signing is part of the same lifecycle model, so document setup, invitation delivery, linked KYC, signing readiness, revocation, expiry, and completion remain visible."
      >
        <EventTable rows={[
          ['SES', 'Email invitation creates a public signing link; pending signing is tracked until the signer completes the document.'],
          ['AES', 'A linked KYC verification must be approved before the document can be signed. The relationship stays visible even after revocation, expiry, or completion.'],
          ['Setup', 'Signature fields and invitation delivery are separate milestones so operations can see whether signing has actually started.'],
          ['Lifecycle', 'Signed, revoked, expired, failed, and pending states feed the same dashboard, audit trail, and usage model.'],
        ]}/>
      </Section>
      <Section
        id="review-audit"
        eyebrow="Review & audit"
        title="Manual review and audit timelines are first-class product surfaces."
        description="Compliance teams can inspect evidence summaries, assign reviewers, leave comments, reopen decisions, and trace webhook or lifecycle events."
        tinted
      >
        <FeatureGrid items={[
          ['Review queues', 'Needs-review sessions surface operational intervention states without mixing them into simple pending work.'],
          ['Audit trail', 'Verification, signing, webhook, retention, and review events are visible in chronological timelines.'],
          ['Revocation and expiry', 'Terminal states do not erase completed milestones; they remain part of the historical workflow.'],
          ['Normalized buckets', 'Pending, action required, completed, and failed buckets power dashboard counts and filters consistently.'],
        ]}/>
      </Section>
      <Section
        id="operations"
        eyebrow="Reservation model"
        title="Sandbox, retention, and usage behavior are explicit."
        description="idemGO separates pending reservation from completed usage, keeps sandbox sessions free, and lets teams control whether raw images are retained."
      >
        <EventTable rows={[
          ['Pending reservation', 'Creating a verification or signing link can reserve usage while the flow is pending. Unused links are not counted as completed usage.'],
          ['Sandbox fixtures', 'Sandbox mode returns deterministic approved, rejected, or review outcomes based on test filenames.'],
          ['Retention controls', 'Raw images are discarded by default; optional retention and review/fraud retention windows are visible in settings.'],
          ['Webhooks', 'Lifecycle events can be sent to your backend with signed headers for verification.'],
        ]}/>
      </Section>
      <FinalCTA/>
    </MarketingShell>
  );
}

export function KycVerificationPage() {
  return (
    <MarketingShell title="KYC Verification for Modern Platforms | idemGO" description="Verify identities securely with hosted KYC flows, document verification, selfie checks, and audit-ready compliance infrastructure built for modern European platforms.">
      <PageHero
        eyebrow="KYC Verification"
        title="Secure identity verification for modern platforms"
        description="Onboard real users, reduce fraud, and streamline compliance with fast, privacy-first KYC verification."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/docs"
        secondaryCtaLabel="View documentation"
      />
      <StatementSection>
        Verification infrastructure designed for modern European platforms.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="KYC verification flow" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">KYC Verification</h2>
            <div className="mt-6">
              <TextStack>
                <p>Digital onboarding should feel seamless for legitimate users — while making it difficult for fraudsters, synthetic identities, and bad actors to enter your platform.</p>
                <p>idemGO provides modern KYC infrastructure designed for fintechs, marketplaces, crypto platforms, HR platforms, legal workflows, and digital businesses that need to verify users securely and efficiently.</p>
                <p>From hosted verification flows to document verification, selfie matching, and audit-ready evidence, idemGO helps teams automate identity verification without building complex compliance systems from scratch.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section id="why-kyc-matters" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Why KYC matters</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Identity verification is no longer optional</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">As online fraud continues to increase, businesses face growing pressure to verify who is accessing their platforms.</p>
          </div>
          <div className="mt-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-center">
            <div className="rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-5">KYC verification helps organizations:</p>
              <BulletList items={[
                'reduce identity fraud',
                'meet regulatory obligations',
                'prevent fake accounts',
                'improve platform trust',
                'create safer onboarding flows',
              ]}/>
            </div>
            <p className="text-lg md:text-xl text-zinc-700 leading-relaxed">
              Whether you are onboarding customers, contractors, business representatives, or signers, reliable identity verification has become a core part of digital infrastructure.
            </p>
          </div>
        </div>
      </section>
      <section id="modern-onboarding" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Built for modern onboarding</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Verification flows designed for real users</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">Traditional KYC systems are often slow, confusing, and heavily manual.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-5">idemGO focuses on creating verification experiences that balance:</p>
              <BulletList items={[
                'compliance',
                'usability',
                'privacy',
                'operational efficiency',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Users can complete verification flows directly from their browser or mobile device without downloading additional software.</p>
                <p>Hosted verification links make it possible to integrate identity verification into existing onboarding flows quickly, while maintaining a consistent and secure experience.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Hosted verification flow"/>
        </div>
      </section>
      <StatementSection tinted>
        Reduce onboarding friction without compromising trust.
      </StatementSection>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Document verification">
              <IllustrationPlaceholder label="Document verification" className="mb-6"/>
            <p>Verify official identity documents including:</p>
            <div className="mt-4">
              <BulletList items={[
                'passports',
                'national ID cards',
                'driver’s licenses',
                'residence permits',
              ]}/>
            </div>
            <p className="mt-4">Structured document data can be extracted and validated automatically to support onboarding and compliance workflows.</p>
          </CopyCard>
          <div id="selfie-liveness">
            <CopyCard title="Selfie & identity matching">
              <IllustrationPlaceholder label="Identity matching" className="mb-6"/>
              <p>Compare identity documents against live selfie captures to help confirm that the person completing the verification matches the provided identity document.</p>
              <p className="mt-3">This additional verification layer helps reduce impersonation attempts and identity misuse.</p>
            </CopyCard>
          </div>
          <CopyCard title="Hosted verification flows">
            <IllustrationPlaceholder label="Hosted verification flow" className="mb-6"/>
            <p>Launch secure verification sessions through hosted flows that can be integrated into onboarding journeys, customer portals, marketplaces, or signing workflows.</p>
            <p className="mt-3">Hosted flows reduce implementation complexity while keeping the user experience streamlined.</p>
          </CopyCard>
          <CopyCard title="Manual review workflows">
            <p>Not every verification can or should be fully automated.</p>
            <p className="mt-3">idemGO includes manual review capabilities for cases that require additional verification, exception handling, or operational oversight.</p>
            <p className="mt-3">Reviewers can access structured verification evidence and audit logs from a centralized dashboard.</p>
          </CopyCard>
          <CopyCard title="Audit trail & evidence">
            <IllustrationPlaceholder label="Audit trail" className="mb-6"/>
            <p>Every verification action is logged through a structured audit trail to support compliance reviews, operational transparency, and internal investigations.</p>
            <p className="mt-4">Verification timelines help teams understand:</p>
            <div className="mt-4">
              <BulletList items={[
                'what happened',
                'when it happened',
                'who performed the action',
                'which evidence was collected',
              ]}/>
            </div>
          </CopyCard>
          </div>
        </div>
      </section>
      <section id="privacy-first-verification" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Privacy-first verification</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Designed with data minimization in mind</h2>
            <div className="mt-6">
              <TextStack>
                <p>Identity verification involves sensitive personal information. idemGO is designed around privacy-conscious verification principles and controlled data handling.</p>
                <p>Rather than treating user identity data as an unlimited data source, idemGO focuses on:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'controlled retention',
                'structured verification evidence',
                'auditability',
                'operational transparency',
                'privacy-aware workflows',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">
              This makes idemGO particularly suited for European organizations and businesses operating in privacy-sensitive environments.
            </p>
          </div>
          <IllustrationPlaceholder label="Audit trail"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Customer onboarding', 'Verify new customers before granting access to financial products, digital services, or regulated environments.'],
              ['Marketplace verification', 'Reduce fake accounts and improve trust between buyers, sellers, contractors, and service providers.'],
              ['Crypto & fintech compliance', 'Support onboarding flows that require stronger identity assurance and audit-ready verification records.'],
              ['Identity-linked signing', 'Connect identity verification with electronic signature workflows for higher-trust document execution.'],
              ['Contractor & workforce verification', 'Verify freelancers, remote workers, contractors, or temporary staff before granting platform or operational access.'],
            ]}/>
          </div>
        </div>
      </section>
      <StatementSection>
        Privacy-first identity verification for digital businesses.
      </StatementSection>
      <section id="developer-friendly-infrastructure" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Developer-friendly infrastructure</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Developer-friendly infrastructure</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is built to integrate into existing platforms and onboarding systems without requiring teams to build verification infrastructure internally.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
            <BulletList items={[
              'hosted verification sessions',
              'API integrations',
              'webhook support',
              'sandbox environments',
              'multi-tenant workflows',
            ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">
              Whether you are building a startup product or scaling operational compliance, idemGO is designed to fit modern product architectures.
            </p>
          </div>
          <IllustrationPlaceholder label="KYC verification flow"/>
        </div>
      </section>
      <KycClosingCta/>
    </MarketingShell>
  );
}

export function AgeVerificationPage() {
  return (
    <MarketingShell title="Privacy-First Age Verification | idemGO" description="Verify age securely while minimizing unnecessary personal data collection with modern age verification infrastructure built for digital platforms.">
      <PageHero
        eyebrow="Age Verification"
        title="Age verification designed for a privacy-first internet"
        description="Confirm age requirements securely while reducing unnecessary data collection and onboarding friction."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="Age verification flow User → age check → approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Age Verification</h2>
            <div className="mt-6">
              <TextStack>
                <p>Many online platforms are required to restrict access based on age.</p>
                <p>Whether for:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'regulated products',
                'online communities',
                'marketplaces',
                'gaming environments',
                'digital services',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>organizations increasingly need reliable ways to verify age while respecting user privacy.</p>
                <p>Traditional onboarding approaches often collect more identity data than necessary, creating unnecessary operational and privacy complexity.</p>
                <p>idemGO helps platforms build privacy-conscious age verification workflows designed to support compliance, trust, and modern user experiences.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <StatementSection>
        Verify age without turning onboarding into full identity surveillance.
      </StatementSection>
      <section id="trust-mechanisms" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Story section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Age-restricted platforms need stronger trust mechanisms</h2>
            <div className="mt-6">
              <TextStack>
                <p>As digital platforms continue to grow, regulators and users increasingly expect organizations to:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'protect minors',
                'reduce harmful access',
                'improve platform safety',
                'respect user privacy',
                'minimize unnecessary data collection',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Age verification should balance:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'trust',
                'compliance',
                'usability',
                'privacy',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Poor onboarding experiences can create friction for legitimate users while increasing operational overhead for platforms.</p>
                <p>idemGO helps organizations create streamlined age verification workflows designed for modern digital environments.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Age verification flow User → age check → approval"/>
        </div>
      </section>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Age-focused verification workflows">
              <p>Support onboarding flows where confirming age eligibility is more important than collecting unnecessary personal identity data.</p>
              <p className="mt-3">This helps organizations create onboarding systems that feel lighter and more privacy-conscious.</p>
            </CopyCard>
            <CopyCard title="Identity & document verification support">
              <IllustrationPlaceholder label="Age verification flow User → age check → approval" className="mb-6"/>
              <p>Age verification workflows can be combined with:</p>
              <div className="mt-4">
                <BulletList items={[
                  'document verification',
                  'selfie verification',
                  'onboarding review workflows',
                  'operational verification processes',
                ]}/>
              </div>
              <p className="mt-4">depending on platform requirements and risk sensitivity.</p>
            </CopyCard>
            <CopyCard title="Privacy-conscious onboarding">
              <p>idemGO focuses on verification workflows designed around:</p>
              <div className="mt-4">
                <BulletList items={[
                  'controlled evidence handling',
                  'data minimization principles',
                  'operational transparency',
                  'structured onboarding visibility',
                ]}/>
              </div>
              <p className="mt-4">This helps organizations reduce unnecessary identity exposure during onboarding.</p>
            </CopyCard>
            <CopyCard title="Operational review workflows">
              <p>Some onboarding cases may require additional review or escalation handling.</p>
              <p className="mt-4">idemGO supports centralized operational workflows for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'exceptions',
                  'suspicious onboarding activity',
                  'manual verification review',
                  'onboarding investigations',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding visibility">
              <IllustrationPlaceholder label="Privacy-first onboarding Age confirmation without excessive data exposure" className="mb-6"/>
              <p>Verification actions and onboarding events are recorded through structured audit trails that support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'operational transparency',
                  'onboarding accountability',
                  'review visibility',
                  'internal investigations',
                  'compliance workflows',
                ]}/>
              </div>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Safer digital platforms start with better onboarding decisions.
      </StatementSection>
      <section id="privacy-platform-trust" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Privacy & platform trust</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Verification infrastructure designed for modern digital environments</h2>
            <div className="mt-6">
              <TextStack>
                <p>Age verification often sits at the intersection of:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'user trust',
                'privacy expectations',
                'onboarding usability',
                'platform responsibility',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>idemGO helps organizations create onboarding systems that support:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'safer user environments',
                'structured operational workflows',
                'privacy-conscious verification',
                'scalable onboarding infrastructure',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">without turning onboarding into a heavy compliance experience.</p>
          </div>
          <IllustrationPlaceholder label="Privacy-first onboarding Age confirmation without excessive data exposure"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Age-restricted marketplaces', 'Support onboarding for platforms selling regulated or age-sensitive products.'],
              ['Gaming & online communities', 'Help reduce underage access while maintaining smooth onboarding experiences.'],
              ['Digital services & subscriptions', 'Introduce lightweight verification flows where age requirements must be enforced.'],
              ['Community & platform safety', 'Strengthen trust and moderation systems through structured onboarding verification.'],
            ]}/>
          </div>
        </div>
      </section>
      <section id="flexible-integration" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible integration</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Flexible integration</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is designed to integrate into modern onboarding systems without requiring teams to build age verification infrastructure internally.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
              <BulletList items={[
                'hosted verification flows',
                'API integrations',
                'webhook support',
                'sandbox environments',
                'operational review workflows',
                'multi-tenant infrastructure',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">Teams can launch onboarding flows quickly while maintaining operational flexibility and privacy-conscious workflows.</p>
          </div>
          <IllustrationPlaceholder label="Age verification flow User → age check → approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build safer onboarding experiences with privacy-first age verification</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to support age-restricted onboarding workflows while maintaining trust, usability, and operational transparency.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function ElectronicSignaturesPage() {
  return (
    <MarketingShell title="Electronic Signatures for Modern Workflows | idemGO" description="Send, sign, and manage electronic signature workflows with identity-linked verification and audit-ready signing infrastructure built for modern platforms.">
      <PageHero
        eyebrow="Electronic Signatures"
        title="Electronic signatures built for modern digital workflows"
        description="Create secure signing experiences with identity-linked verification, audit-ready evidence, and privacy-conscious infrastructure."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="Signing workflow Verification → review → signature → audit trail" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Electronic Signatures</h2>
            <div className="mt-6">
              <TextStack>
                <p>Digital agreements increasingly require more than a simple signature field.</p>
                <p>Organizations need signing workflows that help verify:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'who signed',
                'when the document was signed',
                'how the signing process occurred',
                'which onboarding or verification steps were completed beforehand',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Traditional signing tools often separate identity verification from document execution, creating fragmented workflows and weaker trust signals.</p>
                <p>idemGO combines electronic signatures with structured verification workflows designed for modern onboarding, compliance, and operational processes.</p>
                <p>Whether you are signing agreements, onboarding documents, contracts, approvals, or operational forms, idemGO helps teams create more trustworthy digital signing experiences.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <StatementSection>
        Signing workflows should create trust — not uncertainty.
      </StatementSection>
      <section id="identity-assurance" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Story section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Modern signing requires stronger identity assurance</h2>
            <div className="mt-6">
              <TextStack>
                <p>As digital workflows become more important, organizations increasingly need visibility into:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'signer identity',
                'onboarding context',
                'document integrity',
                'operational accountability',
                'signing timelines',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Simple click-to-sign flows are often insufficient for higher-trust environments.</p>
                <p>idemGO helps organizations create signing workflows that combine:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'structured verification',
                'audit-ready evidence',
                'operational visibility',
                'modern user experiences',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">without introducing unnecessary complexity.</p>
          </div>
          <IllustrationPlaceholder label="Signing workflow Verification → review → signature → audit trail"/>
        </div>
      </section>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Electronic signature workflows">
              <p>Launch signing sessions through structured workflows designed for modern digital onboarding and document processes.</p>
              <p className="mt-4">Support signing experiences for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'contracts',
                  'onboarding documents',
                  'operational approvals',
                  'platform agreements',
                  'business workflows',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Identity-linked signing">
              <IllustrationPlaceholder label="Identity-linked signing Verified signer connected to document workflow" className="mb-6"/>
              <p>Connect document signing workflows to verification and onboarding sessions to create stronger signing assurance.</p>
              <p className="mt-4">This helps organizations better understand:</p>
              <div className="mt-4">
                <BulletList items={[
                  'who completed the signing flow',
                  'which onboarding actions occurred',
                  'how signing events are connected to verification evidence',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Audit-ready evidence">
              <p>Signing events and workflow actions are recorded through structured audit trails that support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'operational transparency',
                  'internal investigations',
                  'onboarding accountability',
                  'compliance workflows',
                  'document review visibility',
                ]}/>
              </div>
              <p className="mt-4">Teams can understand:</p>
              <div className="mt-4">
                <BulletList items={[
                  'when documents were signed',
                  'which onboarding steps occurred',
                  'how the signing workflow progressed',
                  'which evidence was collected',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Structured operational workflows">
              <p>Some signing processes require additional review and operational oversight.</p>
              <p className="mt-4">idemGO supports workflows for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'approval handling',
                  'operational review',
                  'signing escalations',
                  'incomplete signing flows',
                  'onboarding-linked signing processes',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Privacy-conscious document workflows">
              <IllustrationPlaceholder label="Identity-linked signing Verified signer connected to document workflow" className="mb-6"/>
              <p>Signing infrastructure often involves sensitive operational and identity information.</p>
              <p className="mt-4">idemGO focuses on:</p>
              <div className="mt-4">
                <BulletList items={[
                  'structured evidence handling',
                  'controlled retention',
                  'auditability',
                  'operational visibility',
                  'privacy-conscious workflows',
                ]}/>
              </div>
              <p className="mt-4">This helps organizations create trustworthy digital signing systems while maintaining operational flexibility.</p>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Connect verification and signing into one trusted workflow.
      </StatementSection>
      <section id="flexible-signing-infrastructure" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible signing infrastructure</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Built for integration into modern platforms</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is designed to integrate into onboarding systems, internal tools, customer portals, and operational workflows without requiring teams to build signing infrastructure internally.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
              <BulletList items={[
                'hosted signing flows',
                'API integrations',
                'webhook support',
                'sandbox environments',
                'multi-tenant workflows',
                'onboarding-linked document flows',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">Teams can launch signing workflows quickly while maintaining operational control and visibility.</p>
          </div>
          <IllustrationPlaceholder label="Identity-linked signing Verified signer connected to document workflow"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Customer agreements', 'Support digital onboarding flows that require agreements, approvals, or consent collection.'],
              ['Business onboarding', 'Connect company onboarding with operational signing workflows and onboarding documentation.'],
              ['HR & contractor workflows', 'Manage onboarding agreements, operational approvals, and workforce-related signing processes.'],
              ['Platform & marketplace operations', 'Streamline document execution across distributed onboarding and operational environments.'],
            ]}/>
          </div>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build signing workflows your users and teams can trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to create modern electronic signature workflows with stronger operational visibility and identity-linked verification.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function DocumentVerificationPage() {
  return (
    <MarketingShell title="Document Verification for Digital Onboarding | idemGO" description="Verify identity documents and onboarding evidence with structured document verification workflows designed for modern digital platforms.">
      <PageHero
        eyebrow="Document Verification"
        title="Verify documents with greater confidence"
        description="Streamline onboarding and reduce operational friction with modern document verification workflows built for digital platforms."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="Document verification flow Upload → extraction → review → approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Document Verification</h2>
            <div className="mt-6">
              <TextStack>
                <p>Digital onboarding often starts with documents.</p>
                <p>Organizations increasingly need reliable ways to review, validate, and process:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'identity documents',
                'onboarding evidence',
                'supporting files',
                'operational documentation',
                'user-submitted records',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Manual document handling can quickly become fragmented, inconsistent, and difficult to scale.</p>
                <p>idemGO helps teams centralize document verification into structured onboarding workflows designed for operational visibility, review efficiency, and privacy-conscious verification processes.</p>
                <p>Whether you are onboarding customers, businesses, contractors, or signers, idemGO helps create more trustworthy onboarding experiences without building internal verification infrastructure from scratch.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <StatementSection>
        Modern onboarding starts with trustworthy document workflows.
      </StatementSection>
      <section id="document-workflows" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Story section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Document verification is more than OCR extraction</h2>
            <div className="mt-6">
              <TextStack>
                <p>Modern onboarding environments require more than simply uploading files.</p>
                <p>Organizations increasingly need workflows that help:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'organize onboarding evidence',
                'validate identity documents',
                'improve onboarding consistency',
                'reduce operational overhead',
                'support manual review processes',
                'create audit-ready onboarding visibility',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Without structured systems, onboarding workflows often become:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'operationally fragmented',
                'difficult to review',
                'inconsistent across teams',
                'hard to audit at scale',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO helps organizations centralize document verification into modern onboarding infrastructure designed for operational clarity and structured review workflows.</p>
          </div>
          <IllustrationPlaceholder label="Document verification flow Upload → extraction → review → approval"/>
        </div>
      </section>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Identity document support">
              <p>Support onboarding workflows involving:</p>
              <div className="mt-4">
                <BulletList items={[
                  'passports',
                  'national identity cards',
                  'driver’s licenses',
                  'residence permits',
                  'onboarding-related supporting documents',
                ]}/>
              </div>
              <p className="mt-4">Structured onboarding flows help teams review and organize verification evidence more efficiently.</p>
            </CopyCard>
            <CopyCard title="Structured document extraction">
              <IllustrationPlaceholder label="Operational review interface Document evidence, extraction and verification workflow" className="mb-6"/>
              <p>Document workflows can include structured extraction and organization of onboarding information to support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'operational reviews',
                  'onboarding visibility',
                  'verification workflows',
                  'manual investigations',
                  'onboarding consistency',
                ]}/>
              </div>
              <p className="mt-4">This helps reduce fragmented onboarding processes and repetitive manual handling.</p>
            </CopyCard>
            <CopyCard title="Verification-focused review workflows">
              <p>Some onboarding flows require additional operational verification before approval.</p>
              <p className="mt-4">idemGO supports workflows for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'manual review',
                  'escalations',
                  'onboarding exceptions',
                  'suspicious submissions',
                  'incomplete onboarding evidence',
                ]}/>
              </div>
              <p className="mt-4">This helps organizations maintain oversight during onboarding operations.</p>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding evidence">
              <IllustrationPlaceholder label="Operational review interface Document evidence, extraction and verification workflow" className="mb-6"/>
              <p>Verification actions and onboarding events are recorded through structured audit trails that support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'operational transparency',
                  'onboarding accountability',
                  'internal investigations',
                  'compliance workflows',
                  'review visibility',
                ]}/>
              </div>
              <p className="mt-4">Teams can understand:</p>
              <div className="mt-4">
                <BulletList items={[
                  'when documents were submitted',
                  'how onboarding progressed',
                  'which operational actions occurred',
                  'which evidence was reviewed',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Privacy-conscious document handling">
              <p>Document verification often involves highly sensitive information.</p>
              <p className="mt-4">idemGO focuses on:</p>
              <div className="mt-4">
                <BulletList items={[
                  'controlled evidence handling',
                  'structured onboarding visibility',
                  'operational transparency',
                  'privacy-conscious workflows',
                  'controlled retention principles',
                ]}/>
              </div>
              <p className="mt-4">This helps organizations build trustworthy onboarding systems while reducing unnecessary operational complexity.</p>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Structured document workflows for modern onboarding teams.
      </StatementSection>
      <section id="flexible-integration" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible integration</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Designed for modern digital platforms</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is designed to integrate into onboarding systems, operational workflows, and verification infrastructure without requiring teams to build document verification systems internally.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
              <BulletList items={[
                'hosted verification flows',
                'API integrations',
                'webhook support',
                'sandbox environments',
                'operational review systems',
                'multi-tenant workflows',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">Teams can launch verification flows quickly while maintaining operational visibility and onboarding flexibility.</p>
          </div>
          <IllustrationPlaceholder label="Operational review interface Document evidence, extraction and verification workflow"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Customer onboarding', 'Support identity verification and onboarding flows through structured document collection and review.'],
              ['Business onboarding', 'Collect and organize operational onboarding evidence for business verification workflows.'],
              ['Workforce verification', 'Review onboarding documents and supporting evidence for contractors, employees, or temporary workers.'],
              ['Signing workflows', 'Connect document verification to electronic signature workflows for stronger onboarding visibility and operational trust.'],
            ]}/>
          </div>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding workflows around structured document verification</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize document review, improve onboarding visibility, and support modern verification workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function SelfieLivenessPage() {
  return (
    <MarketingShell title="Selfie & Liveness Verification | idemGO" description="Verify that real people are behind every onboarding flow with selfie verification and privacy-first liveness infrastructure built for modern digital platforms.">
      <PageHero
        eyebrow="Selfie & Liveness"
        title="Verify real people — not screenshots, replays, or fake identities"
        description="Strengthen onboarding flows with selfie verification and liveness checks designed to reduce impersonation and identity fraud."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/docs"
        secondaryCtaLabel="View documentation"
      />
      <StatementSection>
        Reduce impersonation risk with identity assurance built for modern onboarding.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="Identity matching" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Selfie & Liveness</h2>
            <div className="mt-6">
              <TextStack>
                <p>Identity verification does not end with document uploads.</p>
                <p>Modern fraud increasingly relies on stolen identity documents, manipulated images, synthetic identities, and impersonation attempts that require stronger identity assurance during onboarding.</p>
                <p>idemGO combines selfie verification and liveness-focused verification flows to help platforms confirm that the person completing the verification process matches the provided identity document.</p>
                <p>The result is a more trustworthy onboarding process with less operational risk and stronger identity confidence.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section id="why-selfie-verification-matters" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Why selfie verification matters</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Documents alone are no longer enough</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-start">
            <div className="rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-5">Fraudsters increasingly use:</p>
              <BulletList items={[
                'stolen identity documents',
                'edited document images',
                'screenshots',
                'replay attacks',
                'synthetic identities',
                'impersonation attempts',
              ]}/>
            </div>
            <div>
              <TextStack>
                <p>Without an additional identity assurance layer, platforms remain vulnerable to fake accounts and onboarding abuse.</p>
                <p>Selfie verification adds a human verification step that helps connect:</p>
              </TextStack>
              <div className="mt-6 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
                <BulletList items={[
                  'the identity document',
                  'the onboarding session',
                  'the actual person behind the process',
                ]}/>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="secure-onboarding-flows" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Built for secure onboarding flows</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Verification designed for real-world usage</h2>
            <div className="mt-6">
              <TextStack>
                <p>Selfie verification should strengthen security without creating unnecessary friction.</p>
                <p>idemGO verification flows are designed to work directly from modern mobile and desktop browsers, allowing users to complete identity checks without downloading separate applications.</p>
                <p>Verification sessions can be launched through hosted flows and integrated into:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'customer onboarding',
                'contractor verification',
                'signing workflows',
                'marketplace onboarding',
                'regulated onboarding environments',
              ]}/>
            </div>
          </div>
          <IllustrationPlaceholder label="Hosted verification flow"/>
        </div>
      </section>
      <StatementSection tinted>
        Real-user verification without turning onboarding into a burden.
      </StatementSection>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Selfie capture">
              <IllustrationPlaceholder label="Identity matching" className="mb-6"/>
              <p>Users can securely capture a live selfie during the verification process as part of identity confirmation workflows.</p>
              <p className="mt-3">This helps strengthen identity assurance during onboarding and signing flows.</p>
            </CopyCard>
            <CopyCard title="Identity matching">
              <IllustrationPlaceholder label="Selfie verification flow" className="mb-6"/>
              <p>Selfie captures can be compared against submitted identity documents to help determine whether the person completing the process matches the provided identity.</p>
              <p className="mt-3">This additional verification layer helps reduce impersonation attempts and account misuse.</p>
            </CopyCard>
            <CopyCard title="Liveness-focused verification flows">
              <p>idemGO verification flows are designed to reduce the risk of:</p>
              <div className="mt-4">
                <BulletList items={[
                  'static image submissions',
                  'screenshot abuse',
                  'replay attempts',
                  'basic spoofing behavior',
                ]}/>
              </div>
              <p className="mt-4">Verification logic can be combined with document verification and operational review workflows for stronger onboarding assurance.</p>
            </CopyCard>
            <CopyCard title="Manual review support">
              <p>Some verification sessions require additional operational oversight.</p>
              <p className="mt-3">idemGO allows reviewers to inspect verification evidence, timelines, and verification outcomes through centralized review interfaces.</p>
              <p className="mt-3">This helps teams manage edge cases and suspicious onboarding attempts more efficiently.</p>
            </CopyCard>
            <CopyCard title="Audit-ready evidence">
              <IllustrationPlaceholder label="Audit trail" className="mb-6"/>
              <p>Verification events are recorded through structured audit trails that support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'operational reviews',
                  'compliance investigations',
                  'onboarding transparency',
                  'internal escalation processes',
                ]}/>
              </div>
              <p className="mt-4">Teams can understand how a verification was completed and which evidence was collected during the process.</p>
            </CopyCard>
          </div>
        </div>
      </section>
      <section id="privacy-conscious-identity-assurance" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Privacy-conscious identity assurance</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Stronger verification without unnecessary data collection</h2>
            <div className="mt-6">
              <TextStack>
                <p>Identity verification should not require unlimited biometric data retention.</p>
                <p>idemGO focuses on privacy-aware verification principles and controlled evidence handling to support organizations operating in privacy-sensitive and compliance-focused environments.</p>
                <p>Verification workflows are designed around:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'structured evidence',
                'operational transparency',
                'controlled retention',
                'privacy-conscious processing',
                'auditability',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">This approach helps platforms strengthen onboarding security while maintaining user trust.</p>
          </div>
          <IllustrationPlaceholder label="Audit trail"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Financial onboarding', 'Reduce impersonation risk during customer onboarding for fintech and regulated platforms.'],
              ['Crypto & high-risk onboarding', 'Add stronger identity assurance to onboarding flows where account abuse and synthetic identities create elevated risk.'],
              ['Identity-linked signing', 'Strengthen electronic signature workflows by connecting signers to verified onboarding sessions.'],
              ['Workforce verification', 'Help confirm the identity of contractors, freelancers, and remote workers before granting operational access.'],
              ['Marketplace trust & safety', 'Reduce fake accounts and improve trust between users, buyers, sellers, and service providers.'],
            ]}/>
          </div>
        </div>
      </section>
      <StatementSection>
        Privacy-first liveness infrastructure for digital businesses.
      </StatementSection>
      <section id="flexible-integration" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible integration</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Flexible integration</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is designed to integrate into existing onboarding and compliance systems without requiring teams to build verification infrastructure internally.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
              <BulletList items={[
                'hosted verification flows',
                'API integrations',
                'webhook support',
                'sandbox environments',
                'multi-tenant workflows',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">Teams can launch verification flows quickly while maintaining flexibility and operational control.</p>
          </div>
          <IllustrationPlaceholder label="Selfie verification flow"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Add stronger identity assurance to your onboarding flows</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Modern platforms need more than document uploads alone.</p>
              <p>Use idemGO to strengthen onboarding security with selfie verification and privacy-conscious identity assurance workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function KybVerificationPage() {
  return (
    <MarketingShell title="KYB Verification for Modern Platforms | idemGO" description="Verify companies, business representatives, and onboarding legitimacy with modern KYB infrastructure designed for compliance and operational trust.">
      <PageHero
        eyebrow="KYB Verification"
        title="Modern business verification for digital platforms"
        description="Verify companies, representatives, and onboarding legitimacy through structured KYB workflows designed for modern business onboarding."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="Business onboarding workflow Company → representatives → review → approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">KYB Verification</h2>
            <div className="mt-6">
              <TextStack>
                <p>Business onboarding is becoming increasingly complex.</p>
                <p>Organizations are expected to verify not only the existence of a company, but also:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'who represents it',
                'who controls it',
                'whether onboarding activity appears legitimate',
                'whether additional compliance review is required',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Traditional onboarding processes are often fragmented across spreadsheets, emails, manual reviews, and disconnected verification systems.</p>
                <p>idemGO helps centralize these workflows into modern business verification infrastructure built for operational trust, onboarding transparency, and scalable compliance processes.</p>
                <p>Whether you are onboarding merchants, vendors, startups, agencies, marketplaces, or corporate customers, idemGO helps teams streamline KYB workflows without building internal verification systems from scratch.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <StatementSection>
        Business onboarding should not feel fragmented.
      </StatementSection>
      <section id="modern-platforms" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Story section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Modern platforms need more than basic company checks</h2>
            <div className="mt-6">
              <TextStack>
                <p>Business verification today involves much more than validating a company registration number.</p>
                <p>Teams increasingly need visibility into:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'business legitimacy',
                'onboarding behavior',
                'representatives',
                'ownership structures',
                'operational context',
                'compliance exposure',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Without structured workflows, onboarding quickly becomes:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'operationally inconsistent',
                'difficult to review',
                'slow to scale',
                'hard to audit',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO helps organizations create centralized KYB workflows that improve onboarding visibility while reducing operational complexity.</p>
          </div>
          <IllustrationPlaceholder label="Business onboarding workflow Company → representatives → review → approval"/>
        </div>
      </section>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Company verification">
              <p>Validate company information during onboarding workflows using structured verification flows designed for operational visibility and onboarding support.</p>
              <p className="mt-4">Capabilities may include:</p>
              <div className="mt-4">
                <BulletList items={[
                  'company registration validation',
                  'onboarding evidence collection',
                  'representative association',
                  'company status visibility',
                  'operational verification workflows',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Representatives & operational visibility">
              <IllustrationPlaceholder label="KYB operational dashboard Representatives, onboarding status and review workflow" className="mb-6"/>
              <p>Associate company representatives, operational contacts, or onboarding participants with verification records and onboarding sessions.</p>
              <p className="mt-4">This helps teams better understand:</p>
              <div className="mt-4">
                <BulletList items={[
                  'who is acting on behalf of the company',
                  'who submitted onboarding information',
                  'how onboarding activity is connected',
                ]}/>
              </div>
              <p className="mt-4">Structured onboarding visibility helps reduce operational blind spots during company verification.</p>
            </CopyCard>
            <CopyCard title="Ownership & transparency context">
              <p>Business onboarding sometimes requires additional visibility into organizational structure and operational control.</p>
              <p className="mt-4">idemGO supports workflows that help teams organize and review:</p>
              <div className="mt-4">
                <BulletList items={[
                  'ownership-related information',
                  'onboarding relationships',
                  'business context',
                  'supporting onboarding evidence',
                ]}/>
              </div>
              <p className="mt-4">This is particularly important for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'fintech',
                  'crypto',
                  'marketplaces',
                  'higher-risk onboarding environments',
                ]}/>
              </div>
            </CopyCard>
            <CopyCard title="Operational review workflows">
              <p>Not every onboarding decision can be fully automated.</p>
              <p className="mt-4">idemGO supports centralized review workflows for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'escalations',
                  'incomplete onboarding submissions',
                  'suspicious onboarding behavior',
                  'higher-risk onboarding cases',
                  'operational exception handling',
                ]}/>
              </div>
              <p className="mt-4">This allows compliance and operations teams to maintain oversight while reducing fragmented manual processes.</p>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding evidence">
              <IllustrationPlaceholder label="KYB operational dashboard Representatives, onboarding status and review workflow" className="mb-6"/>
              <p>Business onboarding actions and verification events are recorded through structured audit trails that support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'onboarding transparency',
                  'operational visibility',
                  'internal investigations',
                  'compliance workflows',
                  'review accountability',
                ]}/>
              </div>
              <p className="mt-4">Teams can understand:</p>
              <div className="mt-4">
                <BulletList items={[
                  'what happened',
                  'when onboarding actions occurred',
                  'which evidence was collected',
                  'which operational decisions were made',
                ]}/>
              </div>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Reduce onboarding friction without reducing trust.
      </StatementSection>
      <section id="privacy-operational-control" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Privacy & operational control</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Structured verification designed for operational clarity</h2>
            <div className="mt-6">
              <TextStack>
                <p>Business onboarding often involves sensitive company and identity information.</p>
                <p>idemGO focuses on:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'structured evidence handling',
                'centralized onboarding visibility',
                'privacy-conscious workflows',
                'controlled retention',
                'operational transparency',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">This helps organizations build scalable onboarding systems without turning KYB into a fragmented operational process.</p>
          </div>
          <IllustrationPlaceholder label="KYB operational dashboard Representatives, onboarding status and review workflow"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Merchant onboarding', 'Verify businesses before granting access to financial, payment, or marketplace infrastructure.'],
              ['Vendor & supplier onboarding', 'Reduce operational risk when onboarding external suppliers, agencies, contractors, or service providers.'],
              ['Marketplace verification', 'Improve trust between businesses and platform participants through structured onboarding workflows.'],
              ['Fintech & regulated onboarding', 'Support onboarding processes that require stronger operational visibility and structured onboarding evidence.'],
              ['B2B platform onboarding', 'Streamline onboarding for startups, agencies, enterprise customers, and operational partners.'],
            ]}/>
          </div>
        </div>
      </section>
      <section id="flexible-integration" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible integration</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Flexible integration</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is designed to integrate into modern onboarding and operational systems without requiring teams to build internal KYB infrastructure from scratch.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
              <BulletList items={[
                'hosted onboarding flows',
                'API integrations',
                'webhook support',
                'sandbox environments',
                'multi-tenant workflows',
                'centralized operational review',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">Teams can launch onboarding workflows quickly while maintaining operational flexibility and oversight.</p>
          </div>
          <IllustrationPlaceholder label="Business onboarding workflow Company → representatives → review → approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build business onboarding workflows your team can trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to streamline company verification, improve onboarding visibility, and centralize operational review workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function AmlSanctionsPage() {
  return (
    <MarketingShell title="AML & Sanctions Screening | idemGO" description="Screen individuals and businesses against sanctions, watchlists, and compliance risk indicators with modern AML infrastructure built for digital onboarding workflows.">
      <PageHero
        eyebrow="AML & Sanctions"
        title="Reduce compliance risk before it reaches your platform"
        description="Strengthen onboarding and compliance workflows with AML and sanctions screening designed for modern digital platforms."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-12 lg:gap-20 items-center">
          <IllustrationPlaceholder label="AML screening workflow Verification → screening → review → approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Intro section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">AML & Sanctions</h2>
            <div className="mt-6">
              <TextStack>
                <p>As digital onboarding becomes faster and more global, compliance teams face increasing pressure to identify financial crime risks earlier in the onboarding process.</p>
                <p>Organizations need visibility into:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'sanctions exposure',
                'politically exposed persons (PEPs)',
                'suspicious onboarding activity',
                'higher-risk entities',
                'compliance-related onboarding risks',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Manual screening processes are often fragmented, inconsistent, and difficult to scale.</p>
                <p>idemGO helps organizations integrate AML and sanctions-focused workflows into structured onboarding infrastructure designed for operational visibility and compliance support.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <StatementSection>
        Compliance workflows should support growth — not slow it down.
      </StatementSection>
      <section id="risk-visibility" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Story section</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Modern onboarding requires stronger risk visibility</h2>
            <div className="mt-6">
              <TextStack>
                <p>Verifying identity alone is often not enough.</p>
                <p>Organizations increasingly need onboarding workflows that help surface:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'sanctions exposure',
                'higher-risk onboarding activity',
                'politically exposed persons',
                'suspicious operational patterns',
                'onboarding inconsistencies',
              ]}/>
            </div>
            <div className="mt-8">
              <TextStack>
                <p>Without structured workflows, compliance reviews can become:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'operationally heavy',
                'difficult to scale',
                'inconsistent across teams',
                'hard to audit',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO helps centralize onboarding visibility into modern verification and operational review workflows.</p>
          </div>
          <IllustrationPlaceholder label="AML screening workflow Verification → screening → review → approval"/>
        </div>
      </section>
      <section id="core-capabilities" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Core capabilities</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Core capabilities</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <CopyCard title="Sanctions screening workflows">
              <p>Support onboarding workflows that require additional screening and operational review processes.</p>
              <p className="mt-4">This may include visibility into:</p>
              <div className="mt-4">
                <BulletList items={[
                  'sanctions exposure',
                  'watchlists',
                  'onboarding risk indicators',
                  'operational escalation flows',
                ]}/>
              </div>
              <p className="mt-4">Structured workflows help teams review onboarding activity more consistently.</p>
            </CopyCard>
            <CopyCard title="Politically exposed persons (PEPs)">
              <IllustrationPlaceholder label="Operational review dashboard Screening, escalations and onboarding review flow" className="mb-6"/>
              <p>Organizations operating in regulated environments may require additional onboarding oversight for politically exposed persons and higher-risk onboarding scenarios.</p>
              <p className="mt-3">idemGO supports workflows that help teams organize and review onboarding evidence in a centralized environment.</p>
            </CopyCard>
            <CopyCard title="Operational review & escalation">
              <p>Not every onboarding case can be automatically approved.</p>
              <p className="mt-4">idemGO supports operational review workflows for:</p>
              <div className="mt-4">
                <BulletList items={[
                  'escalations',
                  'suspicious onboarding activity',
                  'manual investigations',
                  'onboarding exceptions',
                  'additional verification requirements',
                ]}/>
              </div>
              <p className="mt-4">This helps teams maintain operational oversight while reducing fragmented review processes.</p>
            </CopyCard>
            <CopyCard title="Audit-ready compliance visibility">
              <IllustrationPlaceholder label="Operational review dashboard Screening, escalations and onboarding review flow" className="mb-6"/>
              <p>Screening activity and onboarding decisions are recorded through structured audit trails to support:</p>
              <div className="mt-4">
                <BulletList items={[
                  'operational transparency',
                  'internal investigations',
                  'onboarding accountability',
                  'compliance workflows',
                  'review visibility',
                ]}/>
              </div>
              <p className="mt-4">Teams can understand:</p>
              <div className="mt-4">
                <BulletList items={[
                  'what was reviewed',
                  'which onboarding actions occurred',
                  'when decisions were made',
                  'how onboarding workflows progressed',
                ]}/>
              </div>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Structured compliance workflows for modern onboarding teams.
      </StatementSection>
      <section id="privacy-conscious-compliance-infrastructure" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Privacy-conscious compliance infrastructure</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Operational visibility without unnecessary complexity</h2>
            <div className="mt-6">
              <TextStack>
                <p>Compliance workflows often become difficult to manage when operational data, onboarding evidence, and review processes are spread across disconnected systems.</p>
                <p>idemGO focuses on:</p>
              </TextStack>
            </div>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <BulletList items={[
                'centralized onboarding visibility',
                'structured operational workflows',
                'auditability',
                'controlled evidence handling',
                'privacy-conscious infrastructure',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">This helps organizations improve operational clarity while maintaining scalable onboarding processes.</p>
          </div>
          <IllustrationPlaceholder label="Operational review dashboard Screening, escalations and onboarding review flow"/>
        </div>
      </section>
      <section id="common-use-cases" className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Common use cases</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Common use cases</h2>
          </div>
          <div className="mt-12">
            <FeatureGrid items={[
              ['Fintech onboarding', 'Support onboarding workflows that require stronger compliance visibility and operational review.'],
              ['Crypto platforms', 'Add additional onboarding controls for higher-risk onboarding environments and fraud-sensitive workflows.'],
              ['Marketplace verification', 'Improve trust and onboarding oversight for business and user onboarding flows.'],
              ['Regulated platforms', 'Centralize operational review processes and onboarding visibility across compliance-sensitive environments.'],
            ]}/>
          </div>
        </div>
      </section>
      <section id="flexible-integration" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible integration</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08] mt-3">Flexible integration</h2>
            <p className="mt-6 text-sm md:text-base text-zinc-600 leading-relaxed">idemGO is designed to integrate into modern onboarding systems and operational workflows without requiring teams to build compliance infrastructure internally.</p>
            <div className="mt-8 rounded-xl border border-zinc-200/80 bg-white p-6 md:p-7">
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">Capabilities include:</p>
              <BulletList items={[
                'hosted onboarding flows',
                'API integrations',
                'webhook support',
                'sandbox environments',
                'multi-tenant workflows',
                'operational review systems',
              ]}/>
            </div>
            <p className="mt-8 text-sm md:text-base text-zinc-600 leading-relaxed">Teams can integrate onboarding and review workflows while maintaining operational flexibility.</p>
          </div>
          <IllustrationPlaceholder label="AML screening workflow Verification → screening → review → approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding workflows with stronger compliance visibility</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize onboarding reviews, improve operational transparency, and support compliance-focused onboarding workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function CustomerOnboardingPage() {
  return (
    <MarketingShell title="Customer Onboarding Infrastructure | idemGO" description="Streamline customer onboarding with identity verification, document workflows, and audit-ready onboarding infrastructure built for modern digital platforms.">
      <PageHero
        eyebrow="Customer Onboarding"
        title="Customer onboarding built for trust and speed"
        description="Create onboarding workflows that reduce friction, strengthen verification, and support scalable digital growth."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Customer onboarding is often the first real interaction users have with your platform.</p>
              <p>When onboarding is slow or fragmented, legitimate users drop off, operations teams absorb manual work, and fraud risk becomes harder to see clearly.</p>
            </TextStack>
            <TextStack>
              <p>Modern platforms need onboarding systems that balance usability, verification, operational visibility, privacy, and compliance readiness without turning the first user interaction into a heavy process.</p>
              <p>idemGO helps organizations build structured onboarding workflows that combine identity verification, document collection, operational review, and audit-ready onboarding visibility into one modern onboarding experience.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Reduce onboarding friction without compromising trust.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Customer onboarding flow Verification → review → approval → onboarding complete" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern onboarding requires more than account creation</h2>
            <div className="mt-6 space-y-6">
              <TextStack>
                <p>As digital platforms scale, onboarding becomes more than a signup form. It becomes an operational workflow, a trust decision, a compliance process, and a fraud prevention layer happening at the same time.</p>
                <p>Teams need to understand who is onboarding, which steps were completed, what evidence was submitted, and whether additional review is required.</p>
                <p>Without structured systems, those decisions drift into emails, spreadsheets, disconnected verification tools, and manual review processes.</p>
                <p>idemGO helps centralize onboarding into modern infrastructure designed for operational clarity and scalable onboarding workflows.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core capabilities</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <CopyCard title="Identity verification workflows">
              <TextStack>
                <p>Connect onboarding flows to the verification layers that matter most for your platform.</p>
                <BulletList items={[
                  <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>,
                  <Link to="/product/document-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">document verification</Link>,
                  'selfie verification',
                ]}/>
                <p>This helps organizations create stronger onboarding trust while reducing fragmented processes.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Hosted onboarding flows">
              <TextStack>
                <p>Launch onboarding sessions through hosted workflows designed for customer, marketplace, contractor, regulated, and platform verification journeys.</p>
                <BulletList items={[
                  'customer onboarding',
                  'marketplace onboarding',
                  'contractor onboarding',
                ]}/>
                <p>Hosted flows help teams launch onboarding systems quickly without building verification infrastructure internally.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational review & escalations">
              <TextStack>
                <p>Some onboarding flows require additional operational oversight.</p>
                <p>idemGO supports structured review paths for the cases that should not be handled entirely by automation.</p>
                <BulletList items={[
                  'escalations',
                  'onboarding exceptions',
                  'suspicious onboarding activity',
                ]}/>
                <p>This helps teams maintain onboarding visibility while reducing operational complexity.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding visibility">
              <TextStack>
                <p>Onboarding actions and workflow events are recorded through structured audit trails that support transparency, accountability, investigations, compliance workflows, and review visibility.</p>
                <p>Teams can understand the lifecycle of each onboarding decision without reconstructing the process from disconnected tools.</p>
                <BulletList items={[
                  'what onboarding actions occurred',
                  'when reviews happened',
                  'which evidence was collected',
                ]}/>
              </TextStack>
            </CopyCard>
            <CopyCard title="Privacy-conscious onboarding infrastructure">
              <TextStack>
                <p>Modern onboarding systems should not require excessive data collection or fragmented operational handling.</p>
                <p>idemGO focuses on privacy-conscious verification systems that keep evidence structured, retention controlled, and onboarding visibility centralized.</p>
                <BulletList items={[
                  'structured evidence handling',
                  'operational visibility',
                  'controlled retention',
                ]}/>
                <p>This helps organizations create onboarding systems that scale operationally while maintaining user trust.</p>
              </TextStack>
            </CopyCard>
            <IllustrationPlaceholder label="Hosted onboarding session Verification and operational review workflow"/>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Onboarding infrastructure designed for modern digital platforms.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Flexible integration</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">Built for product teams and operational workflows</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems without requiring teams to build internal verification infrastructure from scratch.</p>
                <p>Product and operations teams can combine hosted onboarding flows, API integrations, webhooks, sandbox environments, multi-tenant workflows, and centralized operational review.</p>
                <BulletList items={[
                  'hosted onboarding flows',
                  'API integrations',
                  'webhook support',
                ]}/>
                <p>Teams can launch onboarding workflows quickly while maintaining flexibility and operational control.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Hosted onboarding session Verification and operational review workflow"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Financial services onboarding', 'Support onboarding flows that require stronger verification and operational visibility.'],
              ['Marketplace onboarding', 'Verify users, vendors, or service providers before granting platform access.'],
              ['Workforce onboarding', 'Support onboarding for contractors, freelancers, and temporary workers.'],
              ['Business onboarding', 'Combine company verification and operational onboarding into one centralized workflow.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding workflows users and teams can trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to streamline onboarding, reduce operational friction, and improve onboarding visibility across your platform.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function BusinessOnboardingPage() {
  return (
    <MarketingShell title="Business Onboarding Infrastructure | idemGO" description="Verify companies, business representatives, and onboarding data through structured KYB workflows designed for modern digital platforms.">
      <PageHero
        eyebrow="Business Onboarding"
        title="Business onboarding designed for modern digital operations"
        description="Verify companies, reduce onboarding friction, and centralize operational onboarding workflows through structured KYB infrastructure."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Business onboarding is often significantly more complex than individual onboarding.</p>
              <p>Organizations need to understand company existence, representatives, operational legitimacy, onboarding ownership, and compliance-related information without forcing teams into a fragmented review process.</p>
            </TextStack>
            <TextStack>
              <p>Without structured systems, business onboarding drifts across emails, spreadsheets, manual checks, disconnected verification providers, and operational back-and-forth.</p>
              <p>idemGO helps teams centralize business onboarding workflows through modern KYB infrastructure designed for operational clarity and scalable onboarding operations.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Structured business onboarding for scalable digital platforms.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Business onboarding workflow Company verification → representative review → onboarding approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern platforms need visibility into who they onboard</h2>
            <div className="mt-6 space-y-6">
              <TextStack>
                <p>Business onboarding is no longer limited to enterprise compliance teams.</p>
                <p>Today, marketplaces, fintech platforms, SaaS tools, crypto platforms, and operational businesses increasingly need to verify merchants, vendors, suppliers, agencies, contractors, and business customers.</p>
                <p>Organizations require onboarding systems that help operational teams understand the company, the submitter, the representatives involved, and the evidence collected during onboarding.</p>
                <BulletList items={[
                  'who the company is',
                  'who submitted onboarding',
                  'which representatives are involved',
                ]}/>
                <p>idemGO helps structure these onboarding workflows into centralized operational infrastructure.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core capabilities</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <CopyCard title={<Link to="/product/kyb-verification" className="hover:text-zinc-600">KYB verification workflows</Link>}>
              <TextStack>
                <p>Support onboarding flows that connect company verification, representative handling, operational review, approvals, and structured onboarding evidence.</p>
                <BulletList items={[
                  'company verification',
                  'representative handling',
                  'operational review',
                ]}/>
                <p>KYB workflows help teams reduce fragmented onboarding operations while improving onboarding visibility.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Business representative workflows">
              <TextStack>
                <p>Organizations often need visibility into who is acting on behalf of a company.</p>
                <p>idemGO supports representative-related onboarding workflows for directors, representatives, authorized users, operational contacts, and business onboarding managers.</p>
                <BulletList items={[
                  'directors',
                  'representatives',
                  'authorized users',
                ]}/>
                <p>This helps onboarding teams centralize onboarding accountability and operational review processes.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title={<Link to="/product/aml-sanctions" className="hover:text-zinc-600">AML & sanctions support</Link>}>
              <TextStack>
                <p>Certain onboarding workflows require additional operational screening and risk visibility.</p>
                <p>idemGO supports workflows related to AML checks, sanctions visibility, onboarding escalations, operational review, and suspicious onboarding activity.</p>
                <BulletList items={[
                  'AML checks',
                  'sanctions visibility',
                  'onboarding escalations',
                ]}/>
                <p>This helps organizations create stronger onboarding processes for higher-risk operational environments.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Registry-oriented onboarding support">
              <TextStack>
                <p>Business onboarding workflows often require operational verification against company registration information.</p>
                <p>idemGO is designed to support workflows connected to registry checks, structured company information, operational onboarding evidence, review visibility, and onboarding consistency.</p>
                <BulletList items={[
                  'registry checks',
                  'structured company information',
                  'operational onboarding evidence',
                ]}/>
              </TextStack>
            </CopyCard>
            <CopyCard title="Hosted onboarding infrastructure">
              <TextStack>
                <p>Launch onboarding sessions through hosted workflows designed for vendor, marketplace, B2B, contractor, and operational KYB processes.</p>
                <BulletList items={[
                  'vendor onboarding',
                  'marketplace onboarding',
                  'B2B onboarding',
                ]}/>
                <p>Hosted onboarding helps organizations launch onboarding systems quickly while maintaining centralized operational visibility.</p>
              </TextStack>
            </CopyCard>
            <IllustrationPlaceholder label="Business verification dashboard Representatives, onboarding review, audit visibility"/>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Centralize business onboarding without operational chaos.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Audit-ready onboarding visibility</h2>
            <div className="mt-6">
              <TextStack>
                <p>Business onboarding workflows often involve multiple participants and operational decisions.</p>
                <p>idemGO helps teams track onboarding actions, decisions, representative activity, review workflows, and operational escalations in one structured workflow.</p>
                <BulletList items={[
                  'onboarding actions',
                  'onboarding decisions',
                  'representative activity',
                ]}/>
                <p>Structured audit visibility helps organizations improve operational accountability, onboarding transparency, review consistency, onboarding investigations, and internal governance.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Business verification dashboard Representatives, onboarding review, audit visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Marketplace vendor onboarding', 'Verify sellers, merchants, or providers before granting marketplace access.'],
              ['Financial services onboarding', 'Support onboarding workflows involving regulated businesses and operational review.'],
              ['Contractor & supplier onboarding', 'Centralize onboarding for external companies, suppliers, and operational partners.'],
              ['B2B platform onboarding', 'Create structured onboarding systems for business customers and operational accounts.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for operational and product teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems without requiring organizations to build internal verification infrastructure from scratch.</p>
                <p>Teams can combine hosted onboarding flows, API integrations, webhook support, sandbox environments, multi-tenant onboarding, and centralized operational review.</p>
                <BulletList items={[
                  'hosted onboarding flows',
                  'API integrations',
                  'webhook support',
                ]}/>
                <p>Teams can build onboarding workflows that remain flexible while supporting operational scale.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Business onboarding workflow Company verification → representative review → onboarding approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build business onboarding workflows teams can trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize KYB onboarding, reduce operational friction, and improve onboarding visibility across your platform.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function FraudPreventionPage() {
  return (
    <MarketingShell title="Fraud Prevention Infrastructure | idemGO" description="Reduce onboarding fraud, strengthen operational trust, and improve verification visibility through modern identity infrastructure.">
      <PageHero
        eyebrow="Fraud Prevention"
        title="Fraud prevention built into onboarding infrastructure"
        description="Reduce identity fraud, strengthen verification workflows, and create operational trust across digital onboarding systems."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Fraud prevention is no longer limited to high-risk financial environments.</p>
              <p>Modern digital platforms increasingly face fake identities, onboarding abuse, account misuse, impersonation, operational manipulation, and synthetic onboarding activity.</p>
            </TextStack>
            <TextStack>
              <p>As onboarding workflows scale, fragmented operational systems create visibility gaps that make fraud harder to detect and investigate.</p>
              <p>idemGO helps organizations centralize onboarding trust signals into structured identity and verification workflows designed for modern operational environments.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Trust infrastructure is becoming operational infrastructure.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Identity verification and operational review workflow" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Fraud prevention starts before accounts are approved</h2>
            <div className="mt-6">
              <TextStack>
                <p>Many onboarding systems focus primarily on growth speed while operational review and verification remain fragmented across disconnected systems.</p>
                <p>This creates environments where onboarding visibility becomes inconsistent, suspicious activity is harder to investigate, operational accountability weakens, and onboarding decisions become difficult to trace.</p>
                <p>idemGO helps organizations structure onboarding workflows around verification, review visibility, and operational trust.</p>
                <p>Instead of treating fraud prevention as a separate operational layer, idemGO integrates verification infrastructure directly into onboarding operations.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Verification-driven onboarding">
              <TextStack>
                <p>Identity verification helps organizations reduce onboarding uncertainty before accounts gain operational access.</p>
                <p>idemGO supports workflows involving <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>, <Link to="/product/kyc-verification#selfie-liveness" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">selfie & liveness verification</Link>, document verification, and onboarding review workflows.</p>
                <p>Structured verification workflows help organizations create stronger onboarding trust without introducing unnecessary operational friction.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational review visibility">
              <TextStack>
                <p>Not every onboarding flow can be fully automated.</p>
                <p>Certain onboarding situations require escalations, operational review, additional evidence checks, manual investigations, and suspicious onboarding handling.</p>
                <p>idemGO centralizes review visibility into one operational workflow layer designed for scalable onboarding operations.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready investigations">
              <TextStack>
                <p>Fraud investigations often become difficult when onboarding actions are fragmented across disconnected systems.</p>
                <p>idemGO provides structured audit visibility that helps teams understand onboarding activity, review decisions, verification events, workflow escalations, and operational actions.</p>
                <p>This improves operational accountability, investigation clarity, onboarding transparency, and review consistency.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Reduce onboarding abuse without creating onboarding friction.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for modern digital operations</h2>
            <div className="mt-6">
              <TextStack>
                <p>Fraud prevention increasingly affects marketplaces, fintech platforms, crypto platforms, workforce systems, and operational onboarding environments.</p>
                <p>Organizations need infrastructure that balances onboarding speed, operational visibility, verification confidence, privacy-conscious handling, and scalable review operations.</p>
                <p>idemGO helps teams build onboarding systems designed for operational trust instead of fragmented verification processes.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Operational review dashboard Verification events and onboarding escalations"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Marketplace protection', 'Reduce fake vendor onboarding and suspicious platform activity.'],
              ['Workforce verification', 'Improve contractor and staffing onboarding trust before operational access is granted.'],
              ['Financial onboarding', 'Support stronger onboarding verification and operational review workflows.'],
              ['Platform trust workflows', 'Connect verification infrastructure directly into operational onboarding systems.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for product and operational teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into existing onboarding systems through hosted verification flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <BulletList items={[
                  'hosted verification flows',
                  'APIs',
                  'webhooks',
                ]}/>
                <p>Teams can improve onboarding trust without rebuilding internal onboarding infrastructure from scratch.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Identity verification and operational review workflow"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding systems designed for operational trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to strengthen onboarding visibility, reduce operational fraud exposure, and improve verification-driven onboarding workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function IdentityLinkedSigningPage() {
  return (
    <MarketingShell title="Identity-Linked Signing Infrastructure | idemGO" description="Connect identity verification directly to electronic signing workflows through secure, audit-ready signing infrastructure.">
      <PageHero
        eyebrow="Identity-Linked Signing"
        title="Identity verification connected directly to signing workflows"
        description="Create signing flows where verification, signatures, and operational evidence remain connected inside one structured workflow."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Traditional <Link to="/product/electronic-signatures" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">electronic signing workflows</Link> often operate separately from identity verification systems.</p>
              <p>This creates operational gaps around signer confidence, onboarding visibility, audit consistency, operational review, and evidence handling.</p>
            </TextStack>
            <TextStack>
              <p>Modern digital platforms increasingly require signing workflows that connect signatures to stronger onboarding and identity processes.</p>
              <p>idemGO helps organizations create signing flows where verification and signing remain part of one centralized operational workflow.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Signatures become more powerful when identity and signing remain connected.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Identity verification connected to signing workflow" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern signing workflows require more than document collection</h2>
            <div className="mt-6">
              <TextStack>
                <p>Many organizations no longer view signatures as standalone document actions.</p>
                <p>Instead, signing workflows increasingly form part of onboarding processes, operational approvals, platform access workflows, contractor onboarding, customer verification, and regulated operational flows.</p>
                <p>Organizations require visibility into who completed signing, how verification occurred, which onboarding evidence was collected, when approvals happened, and how operational decisions were made.</p>
                <p>idemGO helps centralize signing and verification into one operational infrastructure layer designed for modern digital workflows.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Verification-linked signing">
              <TextStack>
                <p>idemGO supports workflows where signing can be connected to <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>, document verification, selfie & liveness verification, and onboarding review workflows.</p>
                <BulletList items={[
                  <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>,
                  'document verification',
                  'selfie & liveness verification',
                ]}/>
                <p>This helps organizations strengthen operational confidence around who completed signing workflows.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready signing visibility">
              <TextStack>
                <p>Operational signing workflows often require stronger evidence visibility than traditional e-signature systems provide.</p>
                <p>idemGO helps organizations track signing activity, verification events, onboarding actions, operational reviews, and workflow timelines.</p>
                <p>Structured audit visibility improves operational accountability, review consistency, onboarding transparency, and signing investigations.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Hosted signing workflows">
              <TextStack>
                <p>Launch hosted signing flows without building custom signing infrastructure internally.</p>
                <p>Hosted workflows help teams onboard signers, collect signatures, centralize workflow visibility, simplify operational review, and connect verification and signing.</p>
                <p>This helps organizations launch signing systems quickly while maintaining operational consistency.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Signing workflows should feel operationally trustworthy — not disconnected.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Designed for modern operational workflows</h2>
            <div className="mt-6">
              <TextStack>
                <p>Identity-linked signing is increasingly important for onboarding operations, contractor agreements, customer approvals, platform onboarding, operational authorizations, and regulated workflows.</p>
                <p>Organizations need signing infrastructure that balances usability, verification confidence, operational visibility, audit readiness, and privacy-conscious handling.</p>
                <p>idemGO helps teams centralize these workflows into one structured infrastructure layer.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Signing workflow with verification timeline and audit visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Customer onboarding agreements', 'Connect onboarding verification directly to signing flows.'],
              ['Contractor & workforce agreements', 'Verify operational participants before agreements are completed.'],
              ['Platform authorization workflows', 'Support signing processes connected to onboarding and operational approvals.'],
              ['Internal operational approvals', 'Create centralized signing visibility for operational review workflows.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for modern product teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into operational systems through hosted signing flows, APIs, webhooks, verification workflows, and centralized audit visibility.</p>
                <p>Teams can launch signing workflows without building custom verification and evidence infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Identity verification connected to signing workflow"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build signing workflows users and teams can trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to connect verification and signing into one operationally structured workflow system.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function FinancialServicesPage() {
  return (
    <MarketingShell title="Identity Verification for Financial Services | idemGO" description="Support onboarding, verification, and operational trust workflows for financial platforms through modern identity infrastructure.">
      <PageHero
        eyebrow="Financial Services"
        title="Verification infrastructure for modern financial platforms"
        description="Support onboarding, compliance, and operational trust workflows through modern identity and verification infrastructure."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Financial platforms increasingly operate in environments where onboarding speed, operational trust, and compliance expectations must coexist.</p>
              <p>Organizations need systems that help them onboard customers securely, reduce operational risk, improve onboarding visibility, support verification workflows, and maintain operational accountability.</p>
            </TextStack>
            <TextStack>
              <p>At the same time, onboarding experiences must remain smooth enough for modern digital expectations.</p>
              <p>idemGO helps financial platforms centralize verification, onboarding, and operational review workflows into one structured infrastructure layer designed for modern financial operations, from <Link to="/product/kyb-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYB verification</Link> to <Link to="/product/aml-sanctions" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">AML & sanctions</Link> workflows.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Financial onboarding requires both trust and usability.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Financial onboarding workflow Verification → review → approval → operational access" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern financial onboarding is operational infrastructure</h2>
            <div className="mt-6">
              <TextStack>
                <p>Digital financial services increasingly depend on onboarding systems that connect identity verification, operational review, onboarding visibility, auditability, fraud prevention, and workflow accountability.</p>
                <p>Without structured onboarding infrastructure, operational processes often become fragmented across manual review systems, disconnected onboarding providers, spreadsheets, operational escalations, and inconsistent verification flows.</p>
                <p>idemGO helps organizations structure onboarding and verification into centralized operational workflows designed for scalable financial operations.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Verification-driven onboarding">
              <TextStack>
                <p>Financial onboarding often requires stronger onboarding confidence before operational access is granted.</p>
                <p>idemGO supports workflows involving <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>, document verification, selfie & liveness verification, operational onboarding review, and hosted onboarding sessions.</p>
                <p>Structured onboarding flows help teams create more trustworthy onboarding systems while reducing fragmented operational handling.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational review visibility">
              <TextStack>
                <p>Not every onboarding flow can be fully automated.</p>
                <p>Financial onboarding workflows often require escalations, operational oversight, onboarding investigations, additional review steps, and suspicious onboarding handling.</p>
                <p>idemGO centralizes onboarding visibility into one operational workflow layer designed for scalable onboarding operations.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding infrastructure">
              <TextStack>
                <p>Operational accountability is increasingly important for financial platforms.</p>
                <p>idemGO helps organizations track onboarding actions, verification events, operational reviews, workflow decisions, and onboarding timelines.</p>
                <p>Structured audit visibility helps improve onboarding transparency, operational accountability, internal investigations, review consistency, and operational governance.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Modern financial platforms require structured onboarding visibility.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for digital financial operations</h2>
            <div className="mt-6">
              <TextStack>
                <p>Financial onboarding increasingly affects fintech platforms, payment providers, digital banking products, lending platforms, investment platforms, and operational financial systems.</p>
                <p>Organizations require onboarding systems that balance usability, verification confidence, operational visibility, privacy-conscious handling, and scalable review workflows.</p>
                <p>idemGO helps teams centralize onboarding and verification into one structured infrastructure layer designed for modern financial operations.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Operational review dashboard Verification events and onboarding visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Customer onboarding', 'Support onboarding workflows for financial products and digital financial services.'],
              ['Operational verification', 'Improve onboarding visibility for higher-trust operational environments.'],
              ['Identity-linked approvals', 'Connect onboarding verification to operational approvals and signing workflows.'],
              ['Platform trust workflows', 'Reduce onboarding uncertainty through centralized verification infrastructure.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for modern financial teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems through hosted verification flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can improve onboarding operations without building verification infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Financial onboarding workflow Verification → review → approval → operational access"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding systems designed for modern financial operations</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize verification, improve onboarding visibility, and support operational trust across financial workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function CryptoPage() {
  return (
    <MarketingShell title="Identity Verification for Crypto Platforms | idemGO" description="Support crypto onboarding, verification, and operational trust workflows through modern identity infrastructure built for digital asset platforms.">
      <PageHero
        eyebrow="Crypto"
        title="Verification infrastructure for modern crypto platforms"
        description="Reduce onboarding risk, improve operational visibility, and support trust-focused onboarding workflows for crypto environments."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Crypto platforms operate in environments where onboarding trust, operational visibility, and fraud prevention increasingly shape platform credibility.</p>
              <p>Organizations often need systems that help them verify users, reduce onboarding abuse, support operational reviews, improve onboarding accountability, and strengthen trust workflows.</p>
            </TextStack>
            <TextStack>
              <p>At the same time, onboarding experiences must remain fast and accessible for global digital users.</p>
              <p>idemGO helps crypto platforms centralize onboarding and verification into structured operational infrastructure designed for modern digital asset environments, including <Link to="/product/aml-sanctions" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">AML & sanctions</Link> and <Link to="/use-cases/fraud-prevention" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">fraud prevention</Link> workflows.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Trust becomes infrastructure in digital asset ecosystems.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Crypto onboarding workflow Verification → operational review → approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern crypto onboarding requires operational visibility</h2>
            <div className="mt-6">
              <TextStack>
                <p>As crypto ecosystems mature, onboarding increasingly involves identity verification, operational trust decisions, onboarding reviews, fraud prevention workflows, audit visibility, and onboarding accountability.</p>
                <p>Fragmented onboarding systems often create inconsistent onboarding reviews, operational blind spots, investigation complexity, poor onboarding visibility, and disconnected verification evidence.</p>
                <p>idemGO helps organizations centralize onboarding operations into structured workflows designed for scalable digital asset environments.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Verification-driven onboarding">
              <TextStack>
                <p>Crypto onboarding often requires stronger onboarding confidence before operational access is granted.</p>
                <p>idemGO supports workflows involving <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>, selfie & liveness verification, document verification, operational onboarding review, and hosted onboarding sessions.</p>
                <p>Structured onboarding workflows help platforms reduce fragmented onboarding handling while improving operational visibility.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational review workflows">
              <TextStack>
                <p>Not every onboarding case can be fully automated.</p>
                <p>Crypto platforms often require escalations, suspicious onboarding review, operational investigations, onboarding exception handling, and manual verification review.</p>
                <p>idemGO centralizes onboarding visibility into one operational workflow layer designed for scalable onboarding operations.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding visibility">
              <TextStack>
                <p>Operational accountability is increasingly important for digital asset platforms.</p>
                <p>idemGO helps organizations understand onboarding activity, verification events, operational decisions, onboarding timelines, and review workflows.</p>
                <p>Structured audit visibility helps improve operational transparency, onboarding accountability, internal investigations, workflow consistency, and operational governance.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Modern crypto onboarding requires stronger operational trust.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for evolving digital asset ecosystems</h2>
            <div className="mt-6">
              <TextStack>
                <p>Crypto onboarding increasingly affects exchanges, wallets, token platforms, digital asset marketplaces, Web3 onboarding environments, and operational trust workflows.</p>
                <p>Organizations need onboarding systems that balance usability, onboarding trust, operational visibility, scalable review workflows, and privacy-conscious handling.</p>
                <p>idemGO helps teams centralize onboarding and verification into one operational infrastructure layer designed for modern crypto environments.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Operational onboarding dashboard Verification events and onboarding review visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Exchange onboarding', 'Support onboarding workflows for users accessing trading and digital asset services.'],
              ['Wallet & platform verification', 'Improve onboarding trust for digital asset ecosystems and platform operations.'],
              ['Fraud-sensitive onboarding', 'Strengthen onboarding visibility in environments vulnerable to impersonation and onboarding abuse.'],
              ['Identity-linked operational workflows', 'Connect onboarding verification to approvals, operational access, and platform workflows.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for crypto product teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems through hosted verification flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can improve onboarding trust without building verification infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Crypto onboarding workflow Verification → operational review → approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding systems designed for modern crypto operations</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to improve onboarding visibility, strengthen verification workflows, and support operational trust across digital asset environments.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function MarketplacesPage() {
  return (
    <MarketingShell title="Identity Verification for Marketplaces | idemGO" description="Support marketplace onboarding, seller verification, and operational trust workflows through modern identity infrastructure.">
      <PageHero
        eyebrow="Marketplaces"
        title="Trust infrastructure for modern marketplaces"
        description="Verify users, sellers, and operational participants through onboarding workflows designed for scalable digital marketplaces."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Modern marketplaces increasingly depend on trust between participants who may never interact in person.</p>
              <p>Platforms often need onboarding systems that help them verify sellers, reduce fake accounts, improve onboarding visibility, support operational reviews, strengthen platform trust, and reduce onboarding abuse through <Link to="/use-cases/fraud-prevention" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">fraud prevention</Link> workflows.</p>
            </TextStack>
            <TextStack>
              <p>At scale, fragmented onboarding systems make it difficult to understand who joined the platform, which onboarding steps were completed, which operational actions occurred, and how onboarding decisions were made.</p>
              <p>idemGO helps marketplaces centralize onboarding and verification into structured operational infrastructure designed for modern digital ecosystems.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Marketplace trust starts before transactions happen.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Marketplace onboarding workflow Verification → operational review → marketplace approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Marketplace onboarding increasingly requires operational visibility</h2>
            <div className="mt-6">
              <TextStack>
                <p>As marketplaces scale, onboarding becomes more than account creation.</p>
                <p>Platforms increasingly need systems that help them verify operational participants, structure onboarding workflows, support operational review, investigate suspicious onboarding activity, centralize onboarding evidence, and improve onboarding accountability.</p>
                <p>Without structured onboarding infrastructure, marketplaces often rely on fragmented operational workflows that become difficult to scale consistently.</p>
                <p>idemGO helps organizations centralize onboarding into verification-driven operational systems designed for modern marketplace environments.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Seller & provider onboarding">
              <TextStack>
                <p>Marketplaces often need stronger onboarding confidence before granting operational access.</p>
                <p>idemGO supports workflows involving <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>, document verification, selfie & liveness verification, onboarding review workflows, and hosted onboarding sessions.</p>
                <p>Structured onboarding helps marketplaces improve operational visibility while reducing onboarding fragmentation.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational review workflows">
              <TextStack>
                <p>Not every onboarding case can be automatically approved.</p>
                <p>Marketplace environments often require escalations, suspicious onboarding review, onboarding investigations, operational moderation, and manual onboarding oversight.</p>
                <p>idemGO centralizes onboarding visibility into one operational workflow layer designed for scalable marketplace operations.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding visibility">
              <TextStack>
                <p>Operational accountability becomes increasingly important as marketplaces scale.</p>
                <p>idemGO helps organizations understand onboarding activity, verification events, operational reviews, onboarding timelines, and workflow decisions.</p>
                <p>Structured audit visibility improves onboarding transparency, operational accountability, investigation clarity, moderation consistency, and operational governance.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Stronger onboarding creates stronger marketplace ecosystems.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for modern marketplace operations</h2>
            <div className="mt-6">
              <TextStack>
                <p>Marketplace onboarding increasingly affects sellers, vendors, contractors, providers, agencies, and operational platform participants.</p>
              <p>Organizations require onboarding systems that balance onboarding speed, verification confidence, operational visibility, scalable moderation workflows, privacy-conscious handling, and <Link to="/use-cases/identity-linked-signing" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">identity-linked signing</Link> where approvals or agreements are part of the workflow.</p>
                <p>idemGO helps teams centralize onboarding and operational trust into one structured infrastructure layer designed for digital marketplaces.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Marketplace operations dashboard Verification events and onboarding moderation visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Seller onboarding', 'Verify marketplace sellers before granting operational platform access.'],
              ['Provider verification', 'Support onboarding workflows for contractors, freelancers, and operational participants.'],
              ['Marketplace moderation workflows', 'Improve onboarding visibility and operational review consistency.'],
              ['Platform trust operations', 'Connect verification infrastructure directly into marketplace onboarding systems.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for marketplace product teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems through hosted verification flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can strengthen onboarding trust without building verification infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Marketplace onboarding workflow Verification → operational review → marketplace approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build marketplaces designed around operational trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize onboarding visibility, improve verification workflows, and support scalable marketplace operations.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function HrStaffingPage() {
  return (
    <MarketingShell title="Identity Verification for HR & Staffing | idemGO" description="Support workforce onboarding, contractor verification, and operational trust workflows through modern identity infrastructure.">
      <PageHero
        eyebrow="HR & Staffing"
        title="Verification infrastructure for modern workforce operations"
        description="Streamline workforce onboarding, strengthen operational trust, and centralize onboarding visibility across HR and staffing workflows."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Modern workforce operations increasingly involve distributed onboarding across contractors, freelancers, staffing partners, temporary workers, remote employees, and operational service providers.</p>
              <p>Organizations often need onboarding systems that help them verify identities, structure onboarding workflows, improve onboarding visibility, support operational reviews, and centralize onboarding accountability through <Link to="/use-cases/customer-onboarding" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">customer onboarding</Link>-style infrastructure.</p>
            </TextStack>
            <TextStack>
              <p>Without structured onboarding infrastructure, workforce onboarding can quickly become fragmented across disconnected operational systems and manual review processes.</p>
              <p>idemGO helps organizations centralize workforce onboarding into structured verification and operational workflows designed for modern HR and staffing environments.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Workforce onboarding is becoming operational infrastructure.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Workforce onboarding workflow Verification → onboarding review → operational approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern staffing operations require stronger onboarding visibility</h2>
            <div className="mt-6">
              <TextStack>
                <p>As workforce operations become more distributed, onboarding increasingly involves identity verification, onboarding approvals, operational reviews, contractor onboarding, document handling, and signing workflows.</p>
                <p>Organizations require onboarding systems that help operational teams understand who is onboarding, which onboarding steps were completed, which documents were submitted, which approvals occurred, and how onboarding decisions were made.</p>
                <p>idemGO helps teams centralize onboarding workflows into one structured operational infrastructure layer designed for scalable workforce operations.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <CopyCard title="Workforce verification workflows">
              <TextStack>
                <p>Support onboarding workflows involving contractors, freelancers, temporary workers, staffing operations, and operational workforce onboarding.</p>
                <p>Structured onboarding workflows help organizations improve onboarding consistency while reducing fragmented operational handling.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Identity-linked onboarding">
              <TextStack>
                <p>Workforce onboarding increasingly requires stronger visibility into who completed onboarding workflows and operational approvals.</p>
                <p>idemGO supports workflows connecting identity verification, onboarding documents, operational approvals, signing workflows, and onboarding evidence.</p>
                <p>Teams can connect these workflows with <Link to="/use-cases/identity-linked-signing" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">identity-linked signing</Link> when agreements or approvals are part of onboarding.</p>
                <p>This helps organizations improve onboarding accountability and operational trust.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational review visibility">
              <TextStack>
                <p>Not every onboarding flow can be automatically approved.</p>
                <p>HR and staffing operations often require escalations, onboarding investigations, operational oversight, onboarding exception handling, and manual verification review supported by <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link> workflows.</p>
                <p>idemGO centralizes onboarding visibility into one operational workflow layer designed for scalable workforce operations.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready onboarding visibility">
              <TextStack>
                <p>Operational accountability is increasingly important for workforce onboarding.</p>
                <p>idemGO helps organizations understand onboarding activity, verification events, operational approvals, onboarding timelines, and workflow reviews.</p>
                <p>Structured audit visibility improves onboarding transparency, operational accountability, internal investigations, onboarding consistency, and workforce governance.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Structured onboarding creates stronger workforce operations.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for modern workforce environments</h2>
            <div className="mt-6">
              <TextStack>
                <p>HR and staffing onboarding increasingly affects staffing platforms, operational service providers, contractor onboarding, distributed workforce operations, temporary workforce management, and onboarding coordination.</p>
                <p>Organizations require onboarding systems that balance onboarding speed, operational visibility, verification confidence, scalable review workflows, and privacy-conscious handling.</p>
                <p>idemGO helps teams centralize onboarding and verification into one operational infrastructure layer designed for modern workforce operations.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Operational onboarding dashboard Verification events and onboarding approval visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Contractor onboarding', 'Verify contractors before granting operational access or approving onboarding workflows.'],
              ['Staffing operations', 'Support onboarding workflows across staffing and temporary workforce environments.'],
              ['Operational workforce approvals', 'Connect onboarding verification to approvals, agreements, and onboarding workflows.'],
              ['Distributed workforce onboarding', 'Centralize onboarding visibility across remote and operational workforce environments.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for HR and staffing teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems through hosted verification flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can improve onboarding operations without building verification infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Workforce onboarding workflow Verification → onboarding review → operational approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding systems designed for modern workforce operations</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize onboarding visibility, improve operational trust, and support scalable workforce onboarding workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function SecurityCompliancePage() {
  return (
    <MarketingShell title="Security & Compliance Infrastructure | idemGO" description="Learn how idemGO approaches security, operational visibility, privacy-conscious verification, and compliance-focused onboarding infrastructure.">
      <PageHero
        eyebrow="Security & Compliance"
        title="Security and compliance built into the infrastructure layer"
        description="Modern onboarding systems require operational trust, structured verification, and privacy-conscious infrastructure from the start."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Identity verification and onboarding workflows involve highly sensitive operational and personal information.</p>
              <p>Organizations increasingly need systems that help them protect onboarding data, improve operational visibility, support auditability, structure verification workflows, maintain onboarding accountability, and reduce fragmented operational handling.</p>
            </TextStack>
            <TextStack>
              <p>At the same time, onboarding systems must remain usable, scalable, and operationally manageable.</p>
              <p>idemGO is designed around modern infrastructure principles focused on operational trust, structured verification workflows, and privacy-conscious onboarding architecture.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Trust is not a feature — it is infrastructure.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Verification infrastructure Audit visibility and operational workflow architecture" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern onboarding systems require operational clarity</h2>
            <div className="mt-6">
              <TextStack>
                <p>As onboarding workflows become more digital, organizations increasingly need visibility into verification activity, onboarding actions, operational decisions, review workflows, audit timelines, and evidence handling.</p>
                <p>Without structured infrastructure, onboarding operations often become fragmented across disconnected systems and manual processes.</p>
              <p>idemGO helps organizations centralize onboarding visibility into structured operational workflows designed for modern digital platforms, including <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">KYC verification</Link>, <Link to="/product/aml-sanctions" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">AML & sanctions</Link>, and <Link to="/use-cases/fraud-prevention" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">fraud prevention</Link> workflows.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Privacy-conscious verification workflows">
              <TextStack>
                <p>Identity verification should not require uncontrolled operational complexity or unnecessary data exposure.</p>
                <p>idemGO focuses on structured evidence handling, operational visibility, controlled retention principles, centralized onboarding workflows, and privacy-conscious onboarding systems.</p>
                <p>This helps organizations improve onboarding trust while maintaining operational clarity.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Audit-ready operational visibility">
              <TextStack>
                <p>Operational accountability increasingly matters across onboarding and verification environments.</p>
                <p>idemGO helps organizations understand onboarding activity, verification events, operational reviews, workflow decisions, and onboarding timelines.</p>
                <p>Structured audit visibility helps improve operational transparency, onboarding accountability, internal investigations, workflow consistency, and operational governance.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Centralized operational review">
              <TextStack>
                <p>Not every onboarding flow can be fully automated.</p>
                <p>idemGO supports workflows involving operational review, escalations, onboarding investigations, exception handling, and manual verification processes.</p>
                <p>This helps organizations maintain operational oversight while reducing fragmented review systems.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Security and operational visibility should scale together.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for modern digital operations</h2>
            <div className="mt-6">
              <TextStack>
                <p>Modern onboarding systems increasingly affect financial platforms, marketplaces, workforce operations, crypto environments, operational onboarding systems, and identity-linked workflows.</p>
                <p>Organizations require infrastructure that balances usability, operational trust, onboarding visibility, privacy-conscious handling, and scalable operational workflows.</p>
                <p>idemGO helps organizations centralize these workflows into one structured infrastructure layer designed for modern digital operations.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Operational review system Verification events, onboarding reviews and audit visibility"/>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Operational trust through structured infrastructure</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO is designed around infrastructure principles that support operational visibility, onboarding accountability, structured verification workflows, centralized auditability, and privacy-conscious onboarding systems.</p>
                <p>The platform architecture is designed to support organizations operating in compliance-sensitive and trust-focused environments.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Verification workflows', 'Support onboarding systems requiring structured operational review and audit visibility.'],
              ['Identity-linked operations', 'Connect verification infrastructure to onboarding, signing, and operational approval workflows.'],
              ['Operational investigations', 'Improve onboarding visibility during operational reviews and investigations.'],
              ['Privacy-conscious onboarding', 'Support onboarding systems designed around operational trust and controlled evidence handling.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Flexible infrastructure for modern teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO integrates into onboarding systems through hosted verification flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can improve onboarding trust without building verification infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Verification infrastructure Audit visibility and operational workflow architecture"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding systems designed around operational trust</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to centralize verification visibility, support operational accountability, and improve onboarding trust across digital workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function SystemStatusPage() {
  return (
    <MarketingShell title="System Status | idemGO" description="Monitor platform availability, operational systems, onboarding infrastructure, and verification workflow status for idemGO services.">
      <PageHero
        eyebrow="System Status"
        title="Operational visibility for onboarding infrastructure"
        description="Monitor platform systems, onboarding workflows, and operational infrastructure through centralized status visibility."
        ctaTo="#platform-status"
        ctaLabel="View platform status"
        secondaryCtaTo="/signup"
        secondaryCtaLabel="Start free"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Modern onboarding infrastructure increasingly depends on operational reliability.</p>
              <p>Organizations integrating onboarding, verification, and signing workflows need visibility into platform availability, operational systems, onboarding infrastructure, verification workflows, service reliability, and operational incidents, alongside clear <Link to="/documentation" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">documentation</Link>.</p>
            </TextStack>
            <TextStack>
              <p>idemGO is designed around centralized operational infrastructure intended to support modern onboarding environments and verification workflows.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Operational trust starts with operational visibility.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Operational infrastructure status Verification systems and onboarding workflow availability" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Modern onboarding infrastructure requires transparency</h2>
            <div className="mt-6">
              <TextStack>
                <p>Verification and onboarding systems increasingly become operational dependencies for onboarding flows, customer access, signing workflows, operational reviews, verification visibility, and platform trust systems.</p>
                <p>Organizations need visibility into service availability, operational disruptions, workflow reliability, infrastructure health, and onboarding system status.</p>
                <p>idemGO provides centralized operational status visibility designed for modern onboarding infrastructure environments.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section id="platform-status" className="bg-zinc-50/35 scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <CopyCard title="Platform availability visibility">
              <TextStack>
                <p>Operational onboarding systems increasingly depend on reliable infrastructure availability.</p>
                <p>The status environment is designed to help teams understand onboarding infrastructure status, operational service availability, workflow reliability, onboarding system health, and operational disruptions.</p>
                <p>This helps organizations improve operational awareness around onboarding infrastructure.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Incident communication">
              <TextStack>
                <p>Operational disruptions can affect onboarding visibility and verification workflows.</p>
                <p>Structured incident visibility helps teams understand operational impact, monitor onboarding disruptions, review infrastructure incidents, improve operational awareness, and centralize operational communication.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Operational workflow visibility">
              <TextStack>
                <p>Modern onboarding systems often involve multiple infrastructure components connected together operationally.</p>
                <p>idemGO helps organizations centralize visibility into onboarding workflows, verification systems, operational review systems, signing infrastructure, and event-driven onboarding flows.</p>
                <p>This improves operational clarity across onboarding operations.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Infrastructure visibility improves onboarding reliability.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Designed for operational transparency</h2>
            <div className="mt-6">
              <TextStack>
                <p>Modern onboarding infrastructure increasingly affects <Link to="/use-cases/customer-onboarding" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">customer onboarding</Link>, operational approvals, verification workflows, digital signing systems, onboarding reviews, and operational trust workflows.</p>
                <p>Organizations need operational systems that balance infrastructure reliability, onboarding visibility, operational transparency, scalable workflows, and centralized monitoring.</p>
                <p>idemGO helps teams centralize onboarding infrastructure visibility into one operational layer designed for modern digital operations.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="System monitoring dashboard Operational events and onboarding infrastructure visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Operational visibility areas</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Verification workflows', 'Monitor onboarding and verification infrastructure availability.'],
              ['Signing systems', 'Track operational visibility across signing workflows and infrastructure.'],
              ['Hosted onboarding flows', 'Review hosted onboarding infrastructure status and operational availability.'],
              ['Operational infrastructure', 'Improve visibility across onboarding operations and workflow systems.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Infrastructure designed for modern operational workflows</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO supports operational onboarding infrastructure through hosted onboarding systems, APIs, webhook infrastructure, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can monitor onboarding infrastructure while maintaining operational awareness across digital workflows.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Operational infrastructure status Verification systems and onboarding workflow availability"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Centralize visibility across onboarding infrastructure</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to improve operational visibility, monitor onboarding infrastructure, and support operational trust across digital workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#platform-status" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                View platform status <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </a>
              <Link to="/signup" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Start free
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function DevelopersPage() {
  return (
    <MarketingShell title="Developers · idemGO" description="Build with idemGO APIs, hosted verification links, signed webhooks, sandbox fixtures, lifecycle statuses, and structured verification outcomes.">
      <PageHero
        eyebrow="DEVELOPERS"
        title="A verification API with hosted flows and operational status built in."
        description="Create sessions from your backend, send hosted links to applicants, receive signed webhook events, test with sandbox fixtures, and read normalized lifecycle states."
      />
      <Section
        id="quickstart"
        eyebrow="Quickstart"
        title="Create a session, send the hosted URL, listen for the result."
        description="The fastest integration path is a hosted flow: create a session, share the public URL or let idemGO email it, and consume the status by API or webhook."
      >
        <Timeline steps={[
          ['01', 'Create session', 'POST a verification session with applicant email, reference, and optional claimed identity fields.'],
          ['02', 'Share hosted link', 'Use the returned public URL or enable email delivery.'],
          ['03', 'Applicant completes flow', 'The hosted page captures document evidence and selfie evidence where required.'],
          ['04', 'Read result', 'Use API reads or signed webhooks to update your backend.'],
        ]}/>
      </Section>
      <Section
        id="api"
        eyebrow="API example"
        title="Simple request shapes for verification sessions."
        description="API keys authenticate backend calls. Responses include hosted URLs and session identifiers so your system can track the flow."
        tinted
      >
        <DeveloperPreview/>
      </Section>
      <Section
        id="webhooks"
        eyebrow="Webhook delivery"
        title="Signed lifecycle events for your backend."
        description="Webhook payloads let your product react to completed verification and signing events. The signature header lets your backend verify the event source."
      >
        <CodeBlock>{`X-Idemgo-Signature: t=<unix_ts>,v1=<sha256_hex>

{
  "type": "verification.completed",
  "session": {
    "id": "ver_...",
    "status": "approved",
    "applicant_reference": "user_42"
  }
}`}</CodeBlock>
      </Section>
      <Section
        id="status-model"
        eyebrow="Status model"
        title="Lifecycle statuses map cleanly to operational buckets."
        description="idemGO distinguishes pending work, action-required review, completed outcomes, and terminal failed states so dashboards and filters stay consistent."
        tinted
      >
        <EventTable rows={statusRows}/>
      </Section>
      <Section
        id="sandbox"
        eyebrow="Sandbox"
        title="Deterministic testing without consuming real verification usage."
        description="Sandbox mode is designed for CI, demos, and integration tests. Test filenames can drive approved, rejected, or review outcomes."
      >
        <CodeBlock>{`# Sandbox: filename drives the outcome.
curl -X POST https://idemgo.com/api/public/sessions/<token>/submit \\
  -F "id_document_front=@./approved.jpg" \\
  -F "id_document_back=@./approved.jpg" \\
  -F "selfie=@./any-selfie.jpg"

# -> status: approved, sandbox: true`}</CodeBlock>
      </Section>
      <FinalCTA/>
    </MarketingShell>
  );
}

export function SecurityPage() {
  return (
    <MarketingShell title="Security · idemGO" description="Understand idemGO privacy-first processing, raw image handling, retention controls, signed webhooks, auditability, access controls, and EU-first infrastructure posture.">
      <PageHero
        eyebrow="SECURITY"
        title="Security and privacy choices explained in operational terms."
        description="idemGO is designed for identity workflows where teams need evidence, auditability, and lifecycle control without keeping more sensitive data than necessary."
      />
      <Section
        id="image-handling"
        eyebrow="Privacy-first processing"
        title="Raw images are discarded by default after processing."
        description="Document and selfie images are used for OCR, coded-strip comparison, and face match. By default, idemGO keeps structured evidence and decisions rather than raw image copies."
      >
        <RetentionModelPanel compact/>
      </Section>
      <Section
        id="retention"
        eyebrow="Retention model"
        title="Retention is explicit and visible to operations."
        description="Organizations can keep optional retention off, enable short retention windows, and see retention-related behavior in settings and audit surfaces."
        tinted
      >
        <FeatureGrid items={[
          ['Default posture', 'No raw image retention by default, while structured evidence and audit events remain available.'],
          ['Manual review windows', 'Review workflows can preserve necessary context for operational decisions.'],
          ['Optional retention', 'Organizations can choose retention windows when their review policy requires it.'],
          ['Evidence minimization', 'The system separates evidence summaries from raw media handling.'],
        ]}/>
      </Section>
      <Section
        id="auditability"
        eyebrow="Auditability"
        title="Security and compliance events are part of the workflow record."
        description="The product surfaces lifecycle and review activity so teams can understand what happened without digging through infrastructure logs."
      >
        <EventTable rows={[
          ['Audit trails', 'Verification and signing pages show chronological events, decisions, webhook activity, and review context.'],
          ['Access controls', 'Organization users access dashboards through authenticated workspaces and role-aware controls.'],
          ['API authentication', 'Backend integrations use bearer API keys for API access.'],
          ['Webhook signing', 'Webhook payloads include a signature header so receivers can verify delivery authenticity.'],
          ['EU-first posture', 'Product and privacy defaults are designed with European compliance expectations in mind without limiting the platform to one geography.'],
        ]}/>
      </Section>
      <FinalCTA/>
    </MarketingShell>
  );
}

export function SolutionsPage() {
  return (
    <MarketingShell title="Solutions · idemGO" description="See how fintechs, marketplaces, SaaS platforms, and compliance teams use idemGO for hosted verification, signing, review, and audit workflows.">
      <PageHero
        eyebrow="SOLUTIONS"
        title="Verification workflows for onboarding operations."
        description="idemGO fits teams that need to collect identity evidence, gate access or signing, review exceptions, and synchronize outcomes into their product."
      />
      <section>
        <div className="max-w-[1200px] mx-auto px-6 py-16 md:py-20 grid md:grid-cols-2 gap-6">
          {[
            ['fintech', 'Fintech onboarding', 'Verify users before account access, higher-risk actions, or document signing. Keep audit trails and structured evidence for review.'],
            ['marketplaces', 'Marketplaces', 'Onboard sellers or participants with hosted verification links, review queues, and clear lifecycle states.'],
            ['platforms', 'SaaS platforms', 'Add verification and signing workflows to your product through API-created sessions and hosted flows.'],
            ['compliance', 'Compliance teams', 'Centralize manual review, lifecycle evidence, audit timelines, and retention posture in one workspace.'],
          ].map(([id, title, text]) => (
            <div id={id} key={title} className="border border-zinc-200 rounded-sm bg-white p-6">
              <h2 className="font-display text-xl font-semibold tracking-tight">{title}</h2>
              <p className="text-sm text-zinc-600 leading-relaxed mt-3">{text}</p>
            </div>
          ))}
        </div>
      </section>
      <FinalCTA/>
    </MarketingShell>
  );
}

export function PublicPricingPage() {
  const plans = [
    {
      name: 'Free',
      price: '€0',
      cadence: '/ month',
      description: 'For testing, sandbox workflows, and early product validation.',
      cta: 'Start free',
      action: '/signup',
      featured: false,
      includes: [
        '10 verifications',
        '10 documents',
        'Hosted verification flows',
        'Electronic signatures',
        'Sandbox environment',
        'API access',
        'Webhooks',
        'Manual review dashboard',
      ],
    },
    {
      name: 'Starter',
      price: '€29',
      cadence: '/ month',
      description: 'For early-stage products that need real onboarding workflows.',
      cta: 'Start Starter',
      action: '/signup',
      featured: false,
      includes: [
        '25 verifications',
        '25 documents',
        'KYC verification',
        'Document verification',
        'Electronic signatures',
        'Audit trails',
        'Hosted flows',
        'API & webhooks',
      ],
    },
    {
      name: 'Growth',
      price: '€99',
      cadence: '/ month',
      description: 'For growing platforms with regular onboarding and signing volume.',
      cta: 'Start Growth',
      action: '/signup',
      featured: true,
      includes: [
        '100 verifications',
        '100 documents',
        'KYC & KYB workflows',
        'AML & sanctions workflows',
        'Manual review',
        'Audit-ready evidence',
        'Sandbox + production usage',
        'Priority product support',
      ],
    },
    {
      name: 'Scale',
      price: 'Custom',
      cadence: '',
      description: 'For teams with higher volume, custom onboarding workflows, or advanced operational requirements.',
      cta: 'Contact sales',
      action: 'mailto:support@idemgo.com?subject=idemGO%20sales',
      featured: false,
      includes: [
        'Custom verification volume',
        'Custom document volume',
        'Advanced onboarding workflows',
        'KYB support',
        'Custom retention requirements',
        'Dedicated onboarding support',
        'Volume pricing',
      ],
    },
  ];

  const faqs = [
    ['Can I start for free?', 'Yes. The Free plan includes 10 verifications and 10 documents so you can test onboarding and signing workflows before committing to a paid plan.'],
    ['What happens when I exceed my included usage?', 'Additional usage can be billed separately or upgraded into a higher plan. Additional verifications and documents are expected to start from around €1 per completed usage, with volume discounts available.'],
    ['Are hosted flows included?', 'Yes. Hosted verification and signing flows are included so teams can launch onboarding workflows without building their own capture and signing screens.'],
    ['Is the API included?', 'Yes. API access and webhooks are included so idemGO can connect with your existing onboarding and operational systems.'],
    ['Are KYB and AML included?', 'KYB and AML workflows are positioned inside the Growth and Scale plans because they usually require more operational context and review.'],
    ['Do documents and verifications count separately?', 'Yes. Verification usage and document usage are counted separately so teams can combine identity verification and signing without unclear pricing.'],
    ['Do you offer enterprise pricing?', 'Yes. Scale pricing is available for higher volumes, custom workflows, retention requirements, and operational support.'],
  ];

  return (
    <MarketingShell title="Pricing | idemGO" description="Simple pricing for identity verification, business onboarding, and electronic signing workflows built for modern digital platforms.">
      <PageHero
        eyebrow="Pricing"
        title="Simple pricing for trusted onboarding workflows"
        description="Start with free verification and signing capacity. Scale into production when your onboarding flows grow."
        ctaTo="/signup"
        ctaLabel="Start free"
        secondaryCtaTo="/documentation"
        secondaryCtaLabel="View documentation"
      />
      <section className="bg-white">
        <div className="max-w-[1280px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-4 gap-5">
            {plans.map((plan) => {
              const isSales = plan.action.startsWith('mailto:');
              const ctaClass = plan.featured
                ? 'bg-black text-white hover:bg-zinc-800'
                : 'border border-zinc-200 bg-white hover:border-zinc-400';
              const cardClass = plan.featured
                ? 'border-zinc-900 bg-zinc-950 text-white'
                : 'border-zinc-200/80 bg-white';
              const mutedText = plan.featured ? 'text-zinc-300' : 'text-zinc-600';
              const listText = plan.featured ? 'text-zinc-200' : 'text-zinc-700';
              const bullet = plan.featured ? 'bg-zinc-500' : 'bg-zinc-400';
              return (
                <div key={plan.name} className={`relative rounded-2xl border p-6 md:p-7 ${cardClass}`}>
                  {plan.featured && (
                    <p className="absolute right-5 top-5 text-[10px] font-mono uppercase tracking-widest text-zinc-400">Recommended</p>
                  )}
                  <h2 className="font-display text-2xl font-bold tracking-tight">{plan.name}</h2>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="font-display text-4xl font-bold tracking-tight">{plan.price}</span>
                    {plan.cadence && <span className={`pb-1 text-sm ${mutedText}`}>{plan.cadence}</span>}
                  </div>
                  <p className={`mt-5 text-sm leading-relaxed ${mutedText}`}>{plan.description}</p>
                  <p className={`mt-8 text-[11px] font-mono uppercase tracking-widest ${plan.featured ? 'text-zinc-500' : 'text-zinc-400'}`}>Includes</p>
                  <ul className={`mt-4 grid gap-2.5 text-sm ${listText}`}>
                    {plan.includes.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className={`mt-2 h-1 w-1 shrink-0 rounded-full ${bullet}`}/>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {isSales ? (
                    <a href={plan.action} className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-sm px-4 py-3 text-sm font-medium ${ctaClass}`}>
                      {plan.cta}
                    </a>
                  ) : (
                    <Link to={plan.action} className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-sm px-4 py-3 text-sm font-medium ${ctaClass}`}>
                      {plan.cta}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-sm text-zinc-500">Additional verification: from €1. Additional document: from €1. Volume discounts available.</p>
        </div>
      </section>
      <StatementSection>
        Pricing designed around onboarding capacity — not hidden complexity.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">What counts as usage?</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO pricing is designed around two simple usage types.</p>
                <p>This keeps pricing predictable while allowing teams to combine verification and signing in one platform.</p>
              </TextStack>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <CopyCard title="Verification usage">
              <TextStack>
                <p>A verification is used when a user completes an identity or business verification workflow.</p>
                <p>This may include KYC verification, KYB workflows, age verification, or other onboarding verification flows.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Document usage">
              <TextStack>
                <p>A document is used when a document signing workflow is completed.</p>
                <p>This may include electronic signatures, onboarding documents, agreements, or identity-linked signing workflows.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for testing and production</h2>
            <div className="mt-6">
              <TextStack>
                <p>Every plan is designed to support modern onboarding workflows from early testing to production usage.</p>
                <p>You can start with hosted flows and sandbox environments, then move into production workflows as your product grows.</p>
                <p>idemGO is built for teams that want to launch quickly without building verification, signing, review, and audit infrastructure internally.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Pricing capacity Hosted flows, sandbox and production onboarding usage"/>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">FAQ</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {faqs.map(([question, answer]) => (
              <CopyCard key={question} title={question}>
                <p>{answer}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Start building trusted onboarding workflows</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to verify users, onboard businesses, sign documents, and create audit-ready operational workflows.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/signup" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                Start free <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </Link>
              <a href="mailto:support@idemgo.com?subject=idemGO%20sales" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Contact sales
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

export function PublicDocsPage() {
  return (
    <MarketingShell title="Developer Documentation | idemGO" description="Integrate identity verification, onboarding workflows, and electronic signatures through the idemGO API and hosted infrastructure.">
      <PageHero
        eyebrow="Documentation"
        title="Developer infrastructure for modern onboarding workflows"
        description="Integrate identity verification, onboarding flows, and signing workflows through APIs and hosted infrastructure designed for modern product teams."
        ctaTo="#api-reference"
        ctaLabel="View API reference"
        secondaryCtaTo="/signup"
        secondaryCtaLabel="Start free"
      />
      <section className="bg-white">
        <div className="max-w-[1120px] mx-auto px-6 py-20 md:py-28">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-16 items-start">
            <TextStack>
              <p>Modern onboarding systems increasingly require more than isolated <Link to="/product/kyc-verification" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">verification APIs</Link>.</p>
              <p>Product teams often need infrastructure that helps them launch onboarding workflows quickly, centralize verification operations, support operational reviews, connect <Link to="/use-cases/customer-onboarding" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">customer onboarding</Link> and signing, and reduce internal infrastructure complexity.</p>
            </TextStack>
            <TextStack>
              <p>idemGO provides onboarding infrastructure designed for modern digital products and operational workflows.</p>
              <p>Whether teams want fully hosted onboarding experiences or deeper API integrations, idemGO is designed to integrate into modern operational environments without requiring organizations to build verification infrastructure from scratch.</p>
            </TextStack>
          </div>
        </div>
      </section>
      <StatementSection>
        Modern onboarding infrastructure should feel developer-ready from day one.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
          <IllustrationPlaceholder label="Hosted onboarding flow API → verification → operational review → approval" className="order-2 lg:order-1"/>
          <div className="order-1 lg:order-2">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">APIs are only one part of onboarding infrastructure</h2>
            <div className="mt-6">
              <TextStack>
                <p>Verification systems increasingly involve onboarding workflows, operational reviews, audit visibility, hosted verification sessions, identity-linked signing, <Link to="/product/electronic-signatures" className="underline decoration-zinc-300 underline-offset-4 hover:text-zinc-900">electronic signatures</Link>, and operational trust workflows.</p>
                <p>Building and maintaining this infrastructure internally often creates operational complexity, fragmented onboarding systems, inconsistent review workflows, duplicated onboarding logic, and slower product delivery.</p>
                <p>idemGO helps product teams centralize onboarding infrastructure into structured operational workflows designed for modern digital platforms.</p>
              </TextStack>
            </div>
          </div>
        </div>
      </section>
      <section id="api-reference" className="bg-zinc-50/35 scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Core sections</h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <CopyCard title="Hosted onboarding workflows">
              <TextStack>
                <p>Launch onboarding sessions through hosted verification and signing flows designed for customer onboarding, business onboarding, workforce onboarding, marketplace onboarding, and operational trust workflows.</p>
                <p>Hosted flows help teams reduce implementation complexity while maintaining operational visibility.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="API integrations">
              <TextStack>
                <p>idemGO is designed to integrate into modern product stacks through flexible API infrastructure.</p>
                <p>Teams can connect onboarding systems to verification workflows, onboarding orchestration, operational review systems, signing workflows, and onboarding visibility layers.</p>
                <p>This helps organizations launch onboarding infrastructure faster while maintaining flexibility.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Webhook-driven workflows">
              <TextStack>
                <p>Operational onboarding systems increasingly rely on event-driven infrastructure.</p>
                <p>idemGO supports webhook workflows designed to help teams respond to onboarding events, verification updates, operational review decisions, signing activity, and onboarding status changes.</p>
                <p>Structured event visibility helps organizations centralize onboarding operations more effectively.</p>
              </TextStack>
            </CopyCard>
            <CopyCard title="Sandbox environments">
              <TextStack>
                <p>Product teams need environments where onboarding systems can be tested safely before production rollout.</p>
                <p>idemGO supports sandbox-oriented onboarding workflows that help teams test integrations, simulate onboarding sessions, validate operational flows, review onboarding logic, and iterate faster during implementation.</p>
              </TextStack>
            </CopyCard>
          </div>
        </div>
      </section>
      <StatementSection tinted>
        Infrastructure should reduce operational complexity — not create more of it.
      </StatementSection>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Built for product and operational teams</h2>
            <div className="mt-6">
              <TextStack>
                <p>Modern onboarding systems increasingly affect product operations, onboarding workflows, verification visibility, operational reviews, auditability, and identity-linked approvals.</p>
                <p>Organizations need infrastructure that balances developer flexibility, operational visibility, onboarding trust, scalability, and privacy-conscious handling.</p>
                <p>idemGO helps teams centralize these workflows into one structured infrastructure layer designed for modern digital operations.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Developer integration workflow API events, hosted sessions and operational visibility"/>
        </div>
      </section>
      <section className="bg-zinc-50/35">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common use cases</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              ['Hosted verification integrations', 'Launch onboarding workflows without building custom verification infrastructure internally.'],
              ['Operational onboarding systems', 'Connect onboarding events to operational review and workflow systems.'],
              ['Identity-linked workflows', 'Connect onboarding verification directly to signing and approval flows.'],
              ['Event-driven onboarding', 'Support operational onboarding through webhook-driven infrastructure.'],
            ].map(([title, text]) => (
              <CopyCard key={title} title={title}>
                <p>{text}</p>
              </CopyCard>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Designed for scalable onboarding infrastructure</h2>
            <div className="mt-6">
              <TextStack>
                <p>idemGO supports onboarding infrastructure through hosted onboarding flows, APIs, webhooks, operational review workflows, and centralized onboarding visibility.</p>
                <p>Teams can launch onboarding systems faster while maintaining operational flexibility and visibility.</p>
              </TextStack>
            </div>
          </div>
          <IllustrationPlaceholder label="Hosted onboarding flow API → verification → operational review → approval"/>
        </div>
      </section>
      <section className="idemgo-grid-bg">
        <div className="max-w-[1200px] mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.06] mt-3">Build onboarding infrastructure designed for modern product teams</h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-zinc-600 leading-relaxed max-w-[66ch]">
              <p>Use idemGO to integrate onboarding, verification, and operational workflows into one structured infrastructure layer.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#api-reference" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-sm hover:bg-zinc-800 text-sm font-medium">
                View API reference <ArrowRight className="w-4 h-4" strokeWidth={2}/>
              </a>
              <Link to="/signup" className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-5 py-3 rounded-sm hover:border-zinc-400 text-sm">
                Start free
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
